/**
 * Elite Workflow Templates — Batch 2, Part 3 (Templates 101–130)
 *
 * B2C onboarding, education, fintech, feature adoption, compliance,
 * AI agents, and lifecycle rescue workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 2 PART 3 — Templates 101-130
// =============================================================================

export const ELITE_BATCH2_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 101. B2C ACCOUNT CREATED NO PREFERENCES (6 nodes)
  // ===================================================================

  "B2C Account Created No Preferences": {
    nodes: [
      trigger("App webhook: New B2C account created", "webhook_app", {
        source: "App Backend",
        event: "account.created",
        howItStarts:
          "A new B2C customer creates an account but has not set any preferences after signup",
        setup:
          "Configure your app backend to POST to this workflow URL when a new account is created. Include user_id, email, and signup_timestamp in the payload.",
        inputFields: [],
      }),

      // 2. Wait 24 hours for user to set preferences organically
      n("2", "delayAction", "Wait 24h for organic preference setup", 280, {
        delayData: { delay: 24, delayType: "hours" },
      }),

      // 3. Check if preferences were set
      n("3", "hitApi", "Check user preferences status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/preferences",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "preferences_count",
              customName: "preferences_count",
              type: "number",
            },
            {
              name: "has_categories",
              customName: "has_categories",
              type: "boolean",
            },
            { name: "email", customName: "user_email", type: "string" },
          ],
        },
      }),

      // 4. Condition: preferences still empty?
      n("4", "condition", "Preferences still empty?", 760, {
        conditionData: [
          {
            variable: "preferences_count",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send preference setup nudge email
      emailNode(
        "5",
        "Send preference setup nudge",
        1000,
        "{user_email}",
        "Personalize your experience in 30 seconds",
        "Hi there! We noticed you haven't set your preferences yet. Telling us what you like takes just 30 seconds and makes your experience dramatically better. Click below to set your preferences: {preferences_url}",
      ),

      // 6. Send push notification nudge
      n("6", "pushNotification", "Push: Set your preferences", 1240, {
        pushNotificationDetails: {
          title: "Make it yours",
          body: "Set your preferences now and get content you actually care about. Takes 30 seconds.",
          url: "{preferences_url}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // preferences empty -> send email
      e("5", "6"),
    ],
    summary: `**B2C Account Created No Preferences** — Nudges new B2C users who signed up but never set their content or category preferences.\n\n1. Fires when a new B2C account is created\n2. Waits 24 hours to allow organic preference setup\n3. Checks user preference count via app API\n4. If preferences are still empty, sends a nudge email\n5. Follows up with a push notification to reinforce\n\n**To activate:** Connect your app backend webhook and configure the preferences API endpoint.`,
  },

  // ===================================================================
  // 102. BENEFITS ENROLLMENT GUIDE BOT (8 nodes)
  // ===================================================================

  "Benefits Enrollment Guide Bot": {
    nodes: [
      trigger("HRIS webhook: Benefits enrollment window opens", "webhook_app", {
        source: "BambooHR / Workday",
        event: "benefits.enrollment_open",
        howItStarts:
          "Benefits enrollment period opens for an employee or a new hire becomes eligible",
        setup:
          "Configure your HRIS to POST to this workflow URL when benefits enrollment windows open. Include employee_id, name, email, department, and enrollment_deadline.",
        inputFields: [],
      }),

      // 2. AI: Extract employee details from webhook
      aiNode(
        "2",
        "AI: Parse employee enrollment data",
        280,
        "You are a benefits enrollment data parser. Extract employee details from the webhook payload regardless of field naming. Respond JSON: {employee_name, employee_email, employee_id, department, enrollment_deadline, is_new_hire}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "department", as: "department", type: "string" },
          {
            field: "enrollment_deadline",
            as: "enrollment_deadline",
            type: "string",
          },
          { field: "is_new_hire", as: "is_new_hire", type: "string" },
        ],
      ),

      // 3. Fetch available benefit plans for this employee
      n("3", "hitApi", "Fetch available benefit plans", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourhrplatform.com/v1/employees/{employee_id}/available-benefits",
          headers: [{ key: "Authorization", value: "Bearer {hris_api_key}" }],
          body: [],
          variables: [
            { name: "plans_count", customName: "plans_count", type: "number" },
            {
              name: "plans_summary",
              customName: "plans_summary",
              type: "string",
            },
            {
              name: "has_hsa_option",
              customName: "has_hsa_option",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. AI: Generate personalized benefits guide
      aiNode(
        "4",
        "AI: Generate personalized benefits guide",
        760,
        "You are an HR benefits advisor. Based on the employee's department, new-hire status, and available plans, generate a personalized benefits enrollment guide. Explain each plan simply, highlight recommended options, and include key deadlines. Respond JSON: {guide_html, recommended_plan, key_actions, estimated_monthly_cost}",
        "Employee: {employee_name}, Dept: {department}, New hire: {is_new_hire}, Deadline: {enrollment_deadline}, Available plans: {plans_summary}",
        [
          { field: "guide_html", as: "guide_html", type: "string" },
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
          { field: "key_actions", as: "key_actions", type: "string" },
          {
            field: "estimated_monthly_cost",
            as: "estimated_monthly_cost",
            type: "string",
          },
        ],
      ),

      // 5. Send personalized benefits guide email
      emailNode(
        "5",
        "Send benefits enrollment guide",
        1000,
        "{employee_email}",
        "Your Benefits Enrollment Guide — Deadline: {enrollment_deadline}",
        "{guide_html}<br><br><strong>Recommended Plan:</strong> {recommended_plan}<br><strong>Key Actions:</strong> {key_actions}<br><strong>Estimated Monthly Cost:</strong> {estimated_monthly_cost}",
      ),

      // 6. Delay 3 days then check enrollment status
      n("6", "delayAction", "Wait 3 days", 1240, {
        delayData: { delay: 3, delayType: "days" },
      }),

      // 7. Check enrollment completion
      n("7", "hitApi", "Check enrollment completion", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourhrplatform.com/v1/employees/{employee_id}/enrollment-status",
          headers: [{ key: "Authorization", value: "Bearer {hris_api_key}" }],
          body: [],
          variables: [
            { name: "status", customName: "enrollment_status", type: "string" },
            {
              name: "completed_at",
              customName: "completed_at",
              type: "string",
            },
          ],
        },
      }),

      // 8. Condition: not enrolled yet -> Slack HR alert
      n("8", "condition", "Enrollment completed?", 1720, {
        conditionData: [
          {
            variable: "enrollment_status",
            value: "completed",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Benefits Enrollment Guide Bot** — AI-powered assistant that sends personalized benefits enrollment guides and follows up on incomplete enrollments.\n\n1. Triggered when benefits enrollment window opens in HRIS\n2. AI extracts employee details from webhook payload\n3. Fetches available benefit plans for the specific employee\n4. AI generates a personalized guide with recommendations and cost estimates\n5. Emails the guide with deadline, recommended plan, and key actions\n6. Waits 3 days and checks enrollment completion status\n7. Branches based on whether enrollment is complete for escalation\n\n**To activate:** Connect your HRIS webhook and configure the benefits API endpoints.`,
  },

  // ===================================================================
  // 103. BRANCH BY ACQUISITION SOURCE IN ONBOARDING (8 nodes)
  // ===================================================================

  "Branch by Acquisition Source in Onboarding": {
    nodes: [
      trigger("App webhook: User signs up", "webhook_app", {
        source: "App Backend / Segment",
        event: "user.signed_up",
        howItStarts:
          "A new user signs up and the acquisition source is known via UTM or referral tracking",
        setup:
          "Configure your app backend or Segment to POST signup events to this workflow URL. Include user details and utm_source/utm_medium/referral_code in the payload.",
        inputFields: [],
      }),

      // 2. AI: Extract user and acquisition data
      aiNode(
        "2",
        "AI: Parse signup + acquisition source",
        280,
        "You are a signup data parser. Extract user info and acquisition source from the webhook payload. Determine the acquisition channel: organic, paid_search, paid_social, referral, email_campaign, direct, or partner. Respond JSON: {user_email, user_name, user_id, acquisition_source, utm_source, utm_medium, referral_code}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          {
            field: "acquisition_source",
            as: "acquisition_source",
            type: "string",
          },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "referral_code", as: "referral_code", type: "string" },
        ],
      ),

      // 3. Condition: Is this a paid acquisition?
      n("3", "condition", "Paid acquisition source?", 520, {
        conditionData: [
          {
            variable: "acquisition_source",
            value: "paid_search",
            comparison_operator: "EQUALS",
            logical_operator: "OR",
          },
          {
            variable: "acquisition_source",
            value: "paid_social",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Paid path: Fast-track onboarding with ROI focus
      emailNode(
        "4",
        "Paid: ROI-focused onboarding email",
        760,
        "{user_email}",
        "Welcome {user_name} — Get value in 5 minutes",
        "You came to us looking for a solution — let's make sure you find it fast. Here's your quickstart guide focused on the features that deliver the fastest ROI. Complete setup now: {quickstart_url}",
        380,
      ),

      // 5. Organic/referral path: community-focused onboarding
      emailNode(
        "5",
        "Organic: Community-focused welcome",
        760,
        "{user_email}",
        "Welcome to the community, {user_name}!",
        "We're glad you found us! Here's how other users like you get the most out of our platform. Join our community, explore tutorials, and discover features at your own pace: {community_url}",
        -220,
      ),

      // 6. Tag user in CRM with acquisition data
      n("6", "hitApi", "Tag user in CRM with acquisition source", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{user_email}","firstname":"{user_name}","acquisition_source":"{acquisition_source}","utm_source":"{utm_source}","referral_code":"{referral_code}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 7. Log acquisition event to Mixpanel
      n("7", "hitApi", "Track acquisition in Mixpanel", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.mixpanel.com/track",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Accept", value: "text/plain" },
          ],
          body: [
            {
              key: "data",
              value:
                '[{"event":"onboarding_started","properties":{"distinct_id":"{user_id}","acquisition_source":"{acquisition_source}","utm_source":"{utm_source}","token":"{mixpanel_token}"}}]',
            },
          ],
          variables: [
            { name: "status", customName: "mixpanel_status", type: "number" },
          ],
        },
      }),

      // 8. Slack: Notify growth team of new signup source
      slackNode(
        "8",
        "Slack: Notify growth team of signup",
        1480,
        "New signup from {acquisition_source}: {user_name} ({user_email}). UTM: {utm_source}. CRM ID: {hubspot_contact_id}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // paid -> ROI email
      e("3", "5", "output_2"), // organic/referral -> community email
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Branch by Acquisition Source in Onboarding** — Routes new signups through tailored onboarding paths based on how they found you (paid, organic, referral).\n\n1. Fires on new user signup with UTM/referral data\n2. AI extracts user info and determines acquisition channel\n3. Branches: paid users get ROI-focused fast-track; organic users get community-focused welcome\n4. Tags user in HubSpot CRM with acquisition source metadata\n5. Tracks onboarding start event in Mixpanel\n6. Notifies growth team on Slack with source attribution\n\n**To activate:** Connect your app webhook, HubSpot, Mixpanel, and Slack.`,
  },

  // ===================================================================
  // 104. BRANCH BY DEVICE OS IN ONBOARDING (7 nodes)
  // ===================================================================

  "Branch by Device OS in Onboarding": {
    nodes: [
      trigger("App webhook: User first login detected", "webhook_app", {
        source: "App Backend",
        event: "user.first_login",
        howItStarts:
          "A new user logs in for the first time and the device/OS is detected from their user agent",
        setup:
          "Configure your app backend to POST to this workflow URL on first login. Include user_id, email, user_agent, and device_os in the payload.",
        inputFields: [],
      }),

      // 2. AI: Parse device and OS info
      aiNode(
        "2",
        "AI: Detect device OS from user agent",
        280,
        "You are a device detection parser. Analyze the user agent string and determine the device OS. Categorize as: ios, android, windows, macos, or linux. Respond JSON: {user_email, user_name, user_id, device_os, device_type, is_mobile}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "device_os", as: "device_os", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "is_mobile", as: "is_mobile", type: "string" },
        ],
      ),

      // 3. Condition: Is user on mobile (iOS or Android)?
      n("3", "condition", "Mobile device (iOS/Android)?", 520, {
        conditionData: [
          {
            variable: "is_mobile",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Mobile: Send mobile-optimized onboarding
      emailNode(
        "4",
        "Mobile: App-optimized onboarding",
        760,
        "{user_email}",
        "Your mobile setup guide for {device_os}",
        "Hi {user_name}, we see you're on {device_os}! Here's how to get the best experience on your phone: enable push notifications, add our widget to your home screen, and try these 3 features designed for mobile. Tap here to start: {mobile_guide_url}",
        380,
      ),

      // 5. Desktop: Send desktop-optimized onboarding
      emailNode(
        "5",
        "Desktop: Full-feature onboarding",
        760,
        "{user_email}",
        "Your desktop power-user guide",
        "Hi {user_name}, welcome from {device_os}! Desktop gives you access to our full feature set. Here's your power-user guide: keyboard shortcuts, multi-tab workflows, and integrations that work best on desktop. Start here: {desktop_guide_url}",
        -220,
      ),

      // 6. Push notification: platform-specific tip
      n("6", "pushNotification", "Push: Platform-specific first tip", 1000, {
        pushNotificationDetails: {
          title: "Quick tip for {device_os}",
          body: "Did you know? On {device_os}, you can use {platform_feature} for a faster experience. Try it now!",
          url: "{tip_url}",
        },
      }),

      // 7. Track device OS in analytics
      n("7", "hitApi", "Track device OS in Segment", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/identify",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_id}" },
            {
              key: "traits",
              value:
                '{"device_os":"{device_os}","device_type":"{device_type}","is_mobile":"{is_mobile}","onboarding_path":"device_os_branch"}',
            },
          ],
          variables: [
            { name: "success", customName: "segment_tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // mobile -> mobile onboarding
      e("3", "5", "output_2"), // desktop -> desktop onboarding
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Branch by Device OS in Onboarding** — Detects whether a new user is on mobile or desktop and sends platform-optimized onboarding.\n\n1. Fires on first login with user agent data\n2. AI determines device OS (iOS, Android, Windows, macOS, Linux)\n3. Branches: mobile users get app-optimized guide; desktop users get power-user guide\n4. Sends platform-specific push notification tip\n5. Tracks device OS in Segment for downstream analytics\n\n**To activate:** Connect your app backend webhook and Segment.`,
  },

  // ===================================================================
  // 105. BRANCH BY LANGUAGE OR LOCALE IN ONBOARDING (7 nodes)
  // ===================================================================

  "Branch by Language or Locale in Onboarding": {
    nodes: [
      trigger("App webhook: User signup with locale", "webhook_app", {
        source: "App Backend",
        event: "user.signed_up",
        howItStarts:
          "A new user signs up and their browser locale or selected language is captured",
        setup:
          "Configure your app to POST signup events including Accept-Language header or user-selected locale. Include user_id, email, locale, and timezone in the payload.",
        inputFields: [],
      }),

      // 2. AI: Parse locale and determine language path
      aiNode(
        "2",
        "AI: Detect language and region",
        280,
        "You are a locale parser. From the webhook payload, determine the user's primary language and region. Map to onboarding paths: english, spanish, french, german, portuguese, japanese, or other. Respond JSON: {user_email, user_name, user_id, language_code, language_name, region, timezone, is_english}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "language_code", as: "language_code", type: "string" },
          { field: "language_name", as: "language_name", type: "string" },
          { field: "region", as: "region", type: "string" },
          { field: "is_english", as: "is_english", type: "string" },
        ],
      ),

      // 3. Condition: Is user English-speaking?
      n("3", "condition", "English-speaking user?", 520, {
        conditionData: [
          {
            variable: "is_english",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. English path: standard onboarding
      emailNode(
        "4",
        "English: Standard welcome email",
        760,
        "{user_email}",
        "Welcome to the platform, {user_name}!",
        "Hi {user_name}, welcome! Here's your getting-started guide with everything you need. Click to begin your onboarding: {english_onboarding_url}",
        380,
      ),

      // 5. Non-English: AI-translate onboarding and send localized
      aiNode(
        "5",
        "AI: Translate onboarding for {language_name}",
        760,
        "You are a professional translator. Translate the following onboarding welcome message into {language_name}. Keep the tone friendly and professional. Respond JSON: {translated_subject, translated_body, localized_greeting}",
        "Translate to {language_name} ({language_code}): Subject: Welcome to the platform! Body: Hi there, welcome! Here is your getting-started guide with everything you need. Click to begin your onboarding journey.",
        [
          {
            field: "translated_subject",
            as: "translated_subject",
            type: "string",
          },
          { field: "translated_body", as: "translated_body", type: "string" },
          {
            field: "localized_greeting",
            as: "localized_greeting",
            type: "string",
          },
        ],
        -220,
      ),

      // 6. Send localized email
      emailNode(
        "6",
        "Send localized welcome email",
        1000,
        "{user_email}",
        "{translated_subject}",
        "{localized_greeting} {user_name},<br><br>{translated_body}<br><br><a href='{localized_onboarding_url}'>Start onboarding</a>",
        -220,
      ),

      // 7. Update user profile with locale data
      n("7", "hitApi", "Update user locale in app DB", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.yourapp.com/v1/users/{user_id}/profile",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "language_code", value: "{language_code}" },
            { key: "region", value: "{region}" },
            { key: "timezone", value: "{timezone}" },
            { key: "onboarding_language", value: "{language_name}" },
          ],
          variables: [
            { name: "updated", customName: "profile_updated", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // English -> standard onboarding
      e("3", "5", "output_2"), // non-English -> translate
      e("4", "7"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Branch by Language or Locale in Onboarding** — Detects user language from locale/Accept-Language and delivers onboarding in their native language.\n\n1. Fires on signup with locale data\n2. AI detects primary language and region from payload\n3. English speakers get standard onboarding; others get AI-translated content\n4. Localized welcome email sent in the user's language\n5. User profile updated with locale, region, and timezone\n\n**To activate:** Connect your app backend webhook with locale data.`,
  },

  // ===================================================================
  // 106. BRANCH BY ROLE OR PERSONA IN ONBOARDING (8 nodes)
  // ===================================================================

  "Branch by Role or Persona in Onboarding": {
    nodes: [
      trigger("App webhook: User completes profile with role", "webhook_app", {
        source: "App Backend",
        event: "user.profile_completed",
        howItStarts:
          "A new user selects their role or persona during signup (e.g., developer, marketer, manager, designer)",
        setup:
          "POST to this workflow URL when user selects their role in the onboarding questionnaire. Include user_id, email, name, and selected_role.",
        inputFields: [],
      }),

      // 2. AI: Parse user data and classify persona
      aiNode(
        "2",
        "AI: Classify user persona",
        280,
        "You are a user persona classifier. From the webhook payload, extract the user's selected role and map it to a persona: technical (developer, engineer, data scientist), business (marketer, sales, manager, exec), or creative (designer, content creator, writer). Respond JSON: {user_email, user_name, user_id, selected_role, persona_category, recommended_features, onboarding_complexity}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "selected_role", as: "selected_role", type: "string" },
          { field: "persona_category", as: "persona_category", type: "string" },
          {
            field: "recommended_features",
            as: "recommended_features",
            type: "string",
          },
          {
            field: "onboarding_complexity",
            as: "onboarding_complexity",
            type: "string",
          },
        ],
      ),

      // 3. Condition: Is this a technical persona?
      n("3", "condition", "Technical persona?", 520, {
        conditionData: [
          {
            variable: "persona_category",
            value: "technical",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Technical: API-first onboarding
      emailNode(
        "4",
        "Technical: API & developer onboarding",
        760,
        "{user_email}",
        "Your developer quickstart, {user_name}",
        "Hi {user_name}, as a {selected_role}, here's your path: API docs, SDK installation, CLI tools, and webhook setup. Get your first integration running in 10 minutes. Developer docs: {api_docs_url} | API key: {api_key_url}",
        380,
      ),

      // 5. Business/Creative: guided UI onboarding
      emailNode(
        "5",
        "Business: Guided UI onboarding",
        760,
        "{user_email}",
        "Your personalized setup guide, {user_name}",
        "Hi {user_name}, as a {selected_role}, we recommend starting with: {recommended_features}. Follow our interactive guide to set up your first project in minutes. No code required. Start here: {guided_setup_url}",
        -220,
      ),

      // 6. Configure default dashboard by persona
      n("6", "hitApi", "Configure persona-specific defaults", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/users/{user_id}/onboarding-config",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "persona", value: "{persona_category}" },
            { key: "role", value: "{selected_role}" },
            { key: "recommended_features", value: "{recommended_features}" },
            { key: "complexity", value: "{onboarding_complexity}" },
          ],
          variables: [
            { name: "config_id", customName: "config_id", type: "string" },
            {
              name: "dashboard_url",
              customName: "custom_dashboard_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Send push: your dashboard is ready
      n("7", "pushNotification", "Push: Your custom dashboard is ready", 1240, {
        pushNotificationDetails: {
          title: "Your {selected_role} dashboard is ready",
          body: "We've customized your experience for {selected_role}s. Check it out!",
          url: "{custom_dashboard_url}",
        },
      }),

      // 8. Track persona in Segment
      n("8", "hitApi", "Track persona in Segment", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/identify",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_id}" },
            {
              key: "traits",
              value:
                '{"role":"{selected_role}","persona":"{persona_category}","onboarding_complexity":"{onboarding_complexity}"}',
            },
          ],
          variables: [
            { name: "success", customName: "segment_tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // technical -> dev onboarding
      e("3", "5", "output_2"), // business/creative -> guided onboarding
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Branch by Role or Persona in Onboarding** — Routes new users through persona-specific onboarding (technical, business, creative) based on their selected role.\n\n1. Fires when user selects their role during onboarding\n2. AI classifies persona category and recommends features\n3. Technical users get API/developer-focused onboarding; business/creative get guided UI path\n4. Configures persona-specific dashboard defaults via API\n5. Push notification when custom dashboard is ready\n6. Tracks persona data in Segment for analytics\n\n**To activate:** Connect your app backend webhook and Segment.`,
  },

  // ===================================================================
  // 107. BRANCH BY SUBSCRIPTION PLAN IN ONBOARDING (8 nodes)
  // ===================================================================

  "Branch by Subscription Plan in Onboarding": {
    nodes: [
      trigger("Stripe webhook: Subscription created", "webhook_app", {
        source: "Stripe",
        event: "customer.subscription.created",
        howItStarts:
          "A customer subscribes to a plan (free, starter, pro, enterprise) and onboarding should be tailored to their plan level",
        setup:
          "In Stripe Dashboard -> Developers -> Webhooks, add this workflow URL and subscribe to customer.subscription.created events.",
        inputFields: [],
      }),

      // 2. AI: Extract subscription details
      aiNode(
        "2",
        "AI: Parse Stripe subscription data",
        280,
        "You are a Stripe data parser. Extract customer and subscription details. Determine plan tier: free, starter, pro, or enterprise based on price amount or plan name. Respond JSON: {customer_email, customer_name, customer_id, plan_name, plan_tier, monthly_amount, subscription_id, has_trial}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "plan_tier", as: "plan_tier", type: "string" },
          { field: "monthly_amount", as: "monthly_amount", type: "string" },
          { field: "subscription_id", as: "subscription_id", type: "string" },
        ],
      ),

      // 3. Condition: Enterprise or Pro plan?
      n("3", "condition", "Pro or Enterprise plan?", 520, {
        conditionData: [
          {
            variable: "plan_tier",
            value: "enterprise",
            comparison_operator: "EQUALS",
            logical_operator: "OR",
          },
          {
            variable: "plan_tier",
            value: "pro",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Pro/Enterprise: white-glove onboarding
      emailNode(
        "4",
        "Pro/Enterprise: White-glove welcome",
        760,
        "{customer_email}",
        "Welcome to {plan_name}, {customer_name} — Your dedicated onboarding",
        "Hi {customer_name}, thank you for choosing {plan_name}! As a {plan_tier} customer, you get dedicated onboarding support. Your success manager will reach out within 24 hours to schedule a personalized setup session. In the meantime, explore your premium features: {premium_features_url}",
        380,
      ),

      // 5. Free/Starter: self-serve onboarding
      emailNode(
        "5",
        "Free/Starter: Self-serve onboarding",
        760,
        "{customer_email}",
        "Welcome aboard, {customer_name}! Here's your {plan_name} setup guide",
        "Hi {customer_name}, welcome to {plan_name}! Get started with our interactive setup wizard — it takes about 5 minutes. Follow the step-by-step guide: {self_serve_guide_url}",
        -220,
      ),

      // 6. Provision plan features in app
      n("6", "hitApi", "Provision plan features in app", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/accounts/{customer_id}/provision",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "plan_tier", value: "{plan_tier}" },
            { key: "plan_name", value: "{plan_name}" },
            { key: "subscription_id", value: "{subscription_id}" },
          ],
          variables: [
            {
              name: "account_id",
              customName: "provisioned_account_id",
              type: "string",
            },
            {
              name: "features_enabled",
              customName: "features_enabled",
              type: "string",
            },
          ],
        },
      }),

      // 7. Slack: Notify sales for enterprise signups
      slackNode(
        "7",
        "Slack: Notify sales of new subscription",
        1240,
        "New {plan_tier} subscription: {customer_name} ({customer_email}) on {plan_name} at ${monthly_amount}/mo. Sub ID: {subscription_id}. Features provisioned: {features_enabled}.",
      ),

      // 8. Track subscription start in analytics
      n("8", "hitApi", "Track subscription start in Mixpanel", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.mixpanel.com/track",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Accept", value: "text/plain" },
          ],
          body: [
            {
              key: "data",
              value:
                '[{"event":"subscription_created","properties":{"distinct_id":"{customer_id}","plan_tier":"{plan_tier}","plan_name":"{plan_name}","monthly_amount":"{monthly_amount}","token":"{mixpanel_token}"}}]',
            },
          ],
          variables: [
            { name: "status", customName: "mixpanel_status", type: "number" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // pro/enterprise -> white-glove
      e("3", "5", "output_2"), // free/starter -> self-serve
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Branch by Subscription Plan in Onboarding** — Routes new subscribers through plan-appropriate onboarding (white-glove for Pro/Enterprise, self-serve for Free/Starter).\n\n1. Fires on Stripe subscription.created webhook\n2. AI extracts plan tier, customer details, and pricing\n3. Pro/Enterprise get dedicated onboarding with success manager; Free/Starter get self-serve wizard\n4. Provisions plan features in the application\n5. Notifies sales team on Slack\n6. Tracks subscription event in Mixpanel\n\n**To activate:** Connect your Stripe webhook, app API, Slack, and Mixpanel.`,
  },

  // ===================================================================
  // 108. CANCELED APPOINTMENT REACTIVATION (7 nodes)
  // ===================================================================

  "Canceled Appointment Reactivation": {
    nodes: [
      trigger("Calendar webhook: Appointment canceled", "webhook_app", {
        source: "Calendly / Acuity / Cal.com",
        event: "appointment.canceled",
        howItStarts:
          "A customer cancels a scheduled appointment and needs to be re-engaged to rebook",
        setup:
          "Configure your scheduling tool (Calendly, Acuity, or Cal.com) to POST cancellation events to this workflow URL.",
        inputFields: [],
      }),

      // 2. AI: Extract cancellation details
      aiNode(
        "2",
        "AI: Parse cancellation data",
        280,
        "You are a scheduling data parser. Extract appointment cancellation details from the webhook. Determine if a reason was given and classify urgency. Respond JSON: {customer_email, customer_name, appointment_type, original_date, cancel_reason, cancellation_urgency, provider_name}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "appointment_type", as: "appointment_type", type: "string" },
          { field: "original_date", as: "original_date", type: "string" },
          { field: "cancel_reason", as: "cancel_reason", type: "string" },
          {
            field: "cancellation_urgency",
            as: "cancellation_urgency",
            type: "string",
          },
        ],
      ),

      // 3. Wait 2 hours (not too eager)
      n("3", "delayAction", "Wait 2 hours before re-engage", 520, {
        delayData: { delay: 2, delayType: "hours" },
      }),

      // 4. Send rebook email with easy link
      emailNode(
        "4",
        "Send rebook email",
        760,
        "{customer_email}",
        "We missed you, {customer_name} — Rebook your {appointment_type}",
        "Hi {customer_name}, we noticed you canceled your {appointment_type} originally scheduled for {original_date}. No worries — life happens! When you're ready, rebook in one click: {rebook_url}\n\nIf there's anything we can do to help, just reply to this email.",
      ),

      // 5. Wait 48 hours
      n("5", "delayAction", "Wait 48h for rebook", 1000, {
        delayData: { delay: 48, delayType: "hours" },
      }),

      // 6. Check if rebooked
      n("6", "hitApi", "Check if customer rebooked", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.calendly.com/scheduled_events?user={calendly_user_uri}&invitee_email={customer_email}&status=active",
          headers: [{ key: "Authorization", value: "Bearer {calendly_token}" }],
          body: [],
          variables: [
            {
              name: "collection.length",
              customName: "active_bookings_count",
              type: "number",
            },
          ],
        },
      }),

      // 7. Condition: still no booking -> send SMS nudge
      n("7", "condition", "Still no rebook?", 1480, {
        conditionData: [
          {
            variable: "active_bookings_count",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Canceled Appointment Reactivation** — Re-engages customers who cancel appointments with timed outreach and rebook links.\n\n1. Fires on appointment cancellation from scheduling platform\n2. AI extracts customer details, appointment type, and cancel reason\n3. Waits 2 hours to avoid appearing pushy\n4. Sends empathetic rebook email with one-click scheduling link\n5. Waits 48 hours and checks if customer has rebooked\n6. Branches based on rebook status for further follow-up\n\n**To activate:** Connect your Calendly/Acuity webhook.`,
  },

  // ===================================================================
  // 109. CLUB MEMBERSHIP ONBOARDING (8 nodes)
  // ===================================================================

  "Club Membership Onboarding": {
    nodes: [
      trigger("CRM webhook: New club member joined", "webhook_app", {
        source: "MemberPress / WildApricot",
        event: "member.created",
        howItStarts:
          "A new member joins the club or association and needs to be onboarded with benefits, resources, and community access",
        setup:
          "Configure your membership platform to POST new member events to this workflow URL. Include member_id, email, name, membership_tier, and join_date.",
        inputFields: [],
      }),

      // 2. AI: Parse member data
      aiNode(
        "2",
        "AI: Parse new member data",
        280,
        "You are a membership data parser. Extract new member details from the webhook. Determine membership tier and benefits. Respond JSON: {member_email, member_name, member_id, membership_tier, join_date, interests, referred_by}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "member_id", as: "member_id", type: "string" },
          { field: "membership_tier", as: "membership_tier", type: "string" },
          { field: "join_date", as: "join_date", type: "string" },
          { field: "interests", as: "interests", type: "string" },
          { field: "referred_by", as: "referred_by", type: "string" },
        ],
      ),

      // 3. Provision member access and credentials
      n("3", "hitApi", "Provision member portal access", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourmemberplatform.com/v1/members/{member_id}/provision",
          headers: [
            { key: "Authorization", value: "Bearer {membership_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "tier", value: "{membership_tier}" },
            { key: "interests", value: "{interests}" },
          ],
          variables: [
            {
              name: "portal_url",
              customName: "member_portal_url",
              type: "string",
            },
            {
              name: "member_card_url",
              customName: "member_card_url",
              type: "string",
            },
            {
              name: "benefits_summary",
              customName: "benefits_summary",
              type: "string",
            },
          ],
        },
      }),

      // 4. Send welcome email with benefits package
      emailNode(
        "4",
        "Send welcome email with benefits",
        760,
        "{member_email}",
        "Welcome to the club, {member_name}! Your {membership_tier} benefits inside",
        "Hi {member_name}, welcome aboard as a {membership_tier} member! Here's what you get:\n\n{benefits_summary}\n\nAccess your member portal: {member_portal_url}\nYour digital member card: {member_card_url}\n\nWe're thrilled to have you!",
      ),

      // 5. Add to community platform (Discord/Circle)
      n("5", "hitApi", "Add member to community platform", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://discord.com/api/v10/guilds/{guild_id}/members/{discord_user_id}",
          headers: [
            { key: "Authorization", value: "Bot {discord_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "access_token", value: "{member_discord_token}" },
            { key: "roles", value: "[{membership_tier_role_id}]" },
          ],
          variables: [
            {
              name: "user.id",
              customName: "discord_member_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Wait 3 days
      n("6", "delayAction", "Wait 3 days", 1240, {
        delayData: { delay: 3, delayType: "days" },
      }),

      // 7. Send Day 3: community engagement prompt
      emailNode(
        "7",
        "Day 3: Community engagement prompt",
        1480,
        "{member_email}",
        "3 things to do this week as a new member, {member_name}",
        "Hi {member_name}, here are 3 things every new member should do this week:\n\n1. Introduce yourself in the community\n2. RSVP to the next member event\n3. Explore the member resources library\n\nMembers who do all 3 in their first week are 4x more likely to renew. Let's go: {member_portal_url}",
      ),

      // 8. Slack: Notify membership team
      slackNode(
        "8",
        "Slack: Notify membership team",
        1720,
        "New {membership_tier} member: {member_name} ({member_email}). Referred by: {referred_by}. Interests: {interests}. Portal provisioned, community access granted.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Club Membership Onboarding** — Onboards new club/association members with portal provisioning, benefits delivery, community access, and engagement prompts.\n\n1. Fires when a new member joins via membership platform\n2. AI extracts member details, tier, and interests\n3. Provisions member portal access and digital card\n4. Sends welcome email with full benefits package\n5. Adds member to community platform (Discord) with tier-based roles\n6. Day 3: Sends engagement checklist to drive first-week activity\n7. Notifies membership team on Slack\n\n**To activate:** Connect your membership platform webhook, Discord bot, and Slack.`,
  },

  // ===================================================================
  // 110. COMPLIANCE TRAINING ENROLLMENT BOT (8 nodes)
  // ===================================================================

  "Compliance Training Enrollment Bot": {
    nodes: [
      trigger(
        "HRIS webhook: Employee needs compliance training",
        "webhook_app",
        {
          source: "BambooHR / Workday",
          event: "compliance.training_due",
          howItStarts:
            "An employee is due for required compliance training (annual, new regulation, or new hire requirement)",
          setup:
            "Configure your HRIS or LMS to POST when compliance training is due. Include employee_id, email, training_type, deadline, and compliance_category.",
          inputFields: [],
        },
      ),

      // 2. AI: Parse training requirements
      aiNode(
        "2",
        "AI: Parse compliance training requirements",
        280,
        "You are a compliance training data parser. Extract employee and training details from the webhook. Determine urgency based on deadline proximity. Respond JSON: {employee_email, employee_name, employee_id, training_type, compliance_category, deadline, days_until_deadline, urgency_level, department}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "training_type", as: "training_type", type: "string" },
          {
            field: "compliance_category",
            as: "compliance_category",
            type: "string",
          },
          { field: "deadline", as: "deadline", type: "string" },
          {
            field: "days_until_deadline",
            as: "days_until_deadline",
            type: "number",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 3. Enroll employee in LMS course
      n("3", "hitApi", "Enroll employee in LMS course", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourlms.com/v1/enrollments",
          headers: [
            { key: "Authorization", value: "Bearer {lms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_id", value: "{employee_id}" },
            { key: "course_type", value: "{compliance_category}" },
            { key: "deadline", value: "{deadline}" },
            { key: "mandatory", value: "true" },
          ],
          variables: [
            {
              name: "enrollment_id",
              customName: "enrollment_id",
              type: "string",
            },
            { name: "course_url", customName: "course_url", type: "string" },
            {
              name: "estimated_duration",
              customName: "estimated_duration",
              type: "string",
            },
          ],
        },
      }),

      // 4. Send enrollment notification email
      emailNode(
        "4",
        "Send compliance training enrollment email",
        760,
        "{employee_email}",
        "Action Required: {training_type} — Deadline {deadline}",
        "Hi {employee_name},\n\nYou are required to complete {training_type} ({compliance_category}) by {deadline}.\n\nEstimated time: {estimated_duration}\nStart now: {course_url}\n\nThis training is mandatory. Failure to complete by the deadline may result in account restrictions.\n\nThank you for your prompt attention.",
      ),

      // 5. Condition: Is this high urgency (< 7 days)?
      n("5", "condition", "High urgency (< 7 days)?", 1000, {
        conditionData: [
          {
            variable: "days_until_deadline",
            value: "7",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. High urgency: Also send SMS reminder
      n(
        "6",
        "sendSMS",
        "SMS: Urgent compliance reminder",
        1240,
        {
          sendSmsDetails: {
            to: "{employee_phone}",
            message:
              "URGENT: {training_type} due by {deadline}. Complete now: {course_url}",
          },
        },
        380,
      ),

      // 7. Notify compliance manager on Slack
      slackNode(
        "7",
        "Slack: Notify compliance team",
        1240,
        "Compliance training enrolled: {employee_name} ({employee_email}) for {training_type}. Deadline: {deadline}. Urgency: {urgency_level}. Enrollment ID: {enrollment_id}.",
        -220,
      ),

      // 8. Log compliance action in audit trail
      n("8", "hitApi", "Log compliance action in audit trail", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/compliance/audit-log",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "employee_id", value: "{employee_id}" },
            { key: "action", value: "training_enrolled" },
            { key: "training_type", value: "{training_type}" },
            { key: "enrollment_id", value: "{enrollment_id}" },
            { key: "deadline", value: "{deadline}" },
            { key: "notification_sent", value: "true" },
          ],
          variables: [
            { name: "audit_id", customName: "audit_log_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high urgency -> SMS
      e("5", "7", "output_2"), // normal urgency -> Slack only
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Compliance Training Enrollment Bot** — Auto-enrolls employees in required compliance training, sends deadline-based notifications, and maintains an audit trail.\n\n1. Fires when compliance training is due from HRIS/LMS\n2. AI extracts training requirements and calculates urgency\n3. Enrolls employee in LMS course with deadline\n4. Sends enrollment notification email with course link\n5. High urgency (< 7 days): also sends SMS reminder\n6. Notifies compliance team on Slack\n7. Logs all actions in compliance audit trail\n\n**To activate:** Connect your HRIS/LMS webhook, Slack, and SMS provider.`,
  },

  // ===================================================================
  // 111. CONSUMABLE RUNNING LOW BY USAGE SIGNAL (7 nodes)
  // ===================================================================

  "Consumable Running Low by Usage Signal": {
    nodes: [
      trigger("IoT/App webhook: Usage threshold hit", "webhook_app", {
        source: "IoT Platform / App Backend",
        event: "consumable.threshold_reached",
        howItStarts:
          "A usage signal indicates a consumable product is running low (e.g., printer ink, coffee pods, filters, supplements)",
        setup:
          "Configure your IoT platform or app backend to POST when consumable usage crosses a threshold. Include customer_id, product_sku, current_level, threshold, and estimated_days_remaining.",
        inputFields: [],
      }),

      // 2. AI: Parse usage signal
      aiNode(
        "2",
        "AI: Parse consumable usage signal",
        280,
        "You are a consumable inventory parser. Extract product and customer details from the webhook. Calculate urgency based on days remaining. Respond JSON: {customer_email, customer_name, customer_id, product_name, product_sku, current_level_pct, days_remaining, reorder_url, last_order_date}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_sku", as: "product_sku", type: "string" },
          {
            field: "current_level_pct",
            as: "current_level_pct",
            type: "number",
          },
          { field: "days_remaining", as: "days_remaining", type: "number" },
          { field: "reorder_url", as: "reorder_url", type: "string" },
        ],
      ),

      // 3. Check if auto-reorder is enabled
      n("3", "hitApi", "Check auto-reorder preferences", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourstore.com/v1/customers/{customer_id}/reorder-preferences?sku={product_sku}",
          headers: [{ key: "Authorization", value: "Bearer {store_api_key}" }],
          body: [],
          variables: [
            {
              name: "auto_reorder_enabled",
              customName: "auto_reorder_enabled",
              type: "boolean",
            },
            {
              name: "preferred_quantity",
              customName: "preferred_quantity",
              type: "number",
            },
            {
              name: "payment_method_on_file",
              customName: "payment_on_file",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. Condition: Auto-reorder enabled?
      n("4", "condition", "Auto-reorder enabled?", 760, {
        conditionData: [
          {
            variable: "auto_reorder_enabled",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Auto-reorder: place order automatically
      n(
        "5",
        "hitApi",
        "Place auto-reorder",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.yourstore.com/v1/orders",
            headers: [
              { key: "Authorization", value: "Bearer {store_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "customer_id", value: "{customer_id}" },
              {
                key: "items",
                value:
                  '[{"sku":"{product_sku}","quantity":{preferred_quantity}}]',
              },
              { key: "auto_reorder", value: "true" },
            ],
            variables: [
              { name: "order_id", customName: "order_id", type: "string" },
              { name: "total", customName: "order_total", type: "string" },
              {
                name: "estimated_delivery",
                customName: "estimated_delivery",
                type: "string",
              },
            ],
          },
        },
        380,
      ),

      // 6. Manual path: Send low-stock notification email
      emailNode(
        "6",
        "Send low-stock reorder nudge",
        1000,
        "{customer_email}",
        "Running low on {product_name} — ~{days_remaining} days left",
        "Hi {customer_name}, your {product_name} is at {current_level_pct}% — about {days_remaining} days remaining based on your usage pattern. Reorder now to avoid running out: {reorder_url}\n\nTip: Enable auto-reorder and never worry about running low again.",
        -220,
      ),

      // 7. Push notification: low stock alert
      n("7", "pushNotification", "Push: Consumable running low", 1240, {
        pushNotificationDetails: {
          title: "{product_name} running low",
          body: "~{days_remaining} days left at current usage. Tap to reorder.",
          url: "{reorder_url}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // auto-reorder on -> place order
      e("4", "6", "output_2"), // manual -> send nudge
      e("5", "7"),
      e("6", "7"),
    ],
    summary: `**Consumable Running Low by Usage Signal** — Detects when a consumable product is running low via IoT/usage signals and either auto-reorders or nudges the customer.\n\n1. Fires on usage threshold from IoT platform or app\n2. AI parses product, customer, and days-remaining data\n3. Checks if customer has auto-reorder enabled\n4. Auto-reorder: places order automatically and confirms\n5. Manual: sends low-stock email with reorder link\n6. Sends push notification as a reinforcing alert\n\n**To activate:** Connect your IoT/app backend webhook and store API.`,
  },

  // ===================================================================
  // 112. COURSE ENROLLMENT ASSISTANT BOT (8 nodes)
  // ===================================================================

  "Course Enrollment Assistant Bot": {
    nodes: [
      trigger("LMS webhook: Student enrollment inquiry", "webhook_form", {
        source: "Website Form / LMS",
        event: "enrollment.inquiry",
        howItStarts:
          "A prospective student submits an enrollment inquiry or requests course information",
        setup:
          "Connect your website enrollment form or LMS inquiry endpoint to POST to this workflow URL. Include name, email, course_interest, and any background information.",
        inputFields: [],
      }),

      // 2. AI: Parse inquiry and recommend courses
      aiNode(
        "2",
        "AI: Parse inquiry and match courses",
        280,
        "You are a course enrollment advisor. Extract the student's details and interests. Recommend the best matching courses from the catalog. Respond JSON: {student_email, student_name, course_interest, experience_level, recommended_courses, prerequisites_met, enrollment_url, estimated_cost}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "student_email", as: "student_email", type: "string" },
          { field: "student_name", as: "student_name", type: "string" },
          { field: "course_interest", as: "course_interest", type: "string" },
          { field: "experience_level", as: "experience_level", type: "string" },
          {
            field: "recommended_courses",
            as: "recommended_courses",
            type: "string",
          },
          {
            field: "prerequisites_met",
            as: "prerequisites_met",
            type: "string",
          },
          { field: "enrollment_url", as: "enrollment_url", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "string" },
        ],
      ),

      // 3. Check course availability
      n("3", "hitApi", "Check course availability", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourlms.com/v1/courses/availability?interest={course_interest}",
          headers: [{ key: "Authorization", value: "Bearer {lms_api_key}" }],
          body: [],
          variables: [
            {
              name: "available_seats",
              customName: "available_seats",
              type: "number",
            },
            {
              name: "next_start_date",
              customName: "next_start_date",
              type: "string",
            },
            {
              name: "waitlist_count",
              customName: "waitlist_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Condition: Seats available?
      n("4", "condition", "Seats available?", 760, {
        conditionData: [
          {
            variable: "available_seats",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Available: Send enrollment offer
      emailNode(
        "5",
        "Send enrollment offer email",
        1000,
        "{student_email}",
        "Great news, {student_name}! Seats available for {course_interest}",
        "Hi {student_name},\n\nBased on your interest in {course_interest} and your {experience_level} background, we recommend:\n\n{recommended_courses}\n\nSeats available: {available_seats}\nNext start date: {next_start_date}\nEstimated cost: {estimated_cost}\n\nEnroll now: {enrollment_url}\n\nPrerequisites: {prerequisites_met}",
        380,
      ),

      // 6. Waitlist: Add to waitlist and notify
      emailNode(
        "6",
        "Send waitlist notification",
        1000,
        "{student_email}",
        "{student_name}, you're on the waitlist for {course_interest}",
        "Hi {student_name},\n\nThe {course_interest} course is currently full, but we've added you to the waitlist (position: {waitlist_count}). We'll notify you as soon as a seat opens.\n\nIn the meantime, check out these related courses: {recommended_courses}",
        -220,
      ),

      // 7. Create student lead in CRM
      n("7", "hitApi", "Create student lead in CRM", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{student_email}","firstname":"{student_name}","course_interest":"{course_interest}","experience_level":"{experience_level}","enrollment_status":"inquiry"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_contact_id", type: "string" },
          ],
        },
      }),

      // 8. Slack: Notify admissions team
      slackNode(
        "8",
        "Slack: Notify admissions team",
        1480,
        "New enrollment inquiry: {student_name} ({student_email}) interested in {course_interest}. Level: {experience_level}. Seats: {available_seats}. CRM: {crm_contact_id}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // seats available -> enrollment offer
      e("4", "6", "output_2"), // no seats -> waitlist
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Course Enrollment Assistant Bot** — AI-powered enrollment advisor that matches students to courses, checks availability, and handles waitlisting.\n\n1. Fires on enrollment inquiry form submission\n2. AI analyzes student interests and recommends courses\n3. Checks real-time seat availability\n4. Available: sends enrollment offer; Full: adds to waitlist\n5. Creates student lead in CRM with interest data\n6. Notifies admissions team on Slack\n\n**To activate:** Connect your LMS, HubSpot, and Slack.`,
  },

  // ===================================================================
  // 113. COURSE SUBSCRIPTION RENEWAL (7 nodes)
  // ===================================================================

  "Course Subscription Renewal": {
    nodes: [
      trigger("Schedule: Daily renewal check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Daily check for course subscriptions expiring within 14 days",
        setup:
          "This workflow runs automatically on schedule. Configure your LMS API endpoint to return subscriptions nearing expiry.",
        inputFields: [],
      }),

      // 2. Fetch expiring subscriptions
      n("2", "hitApi", "Fetch expiring course subscriptions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourlms.com/v1/subscriptions?expires_within_days=14&status=active",
          headers: [{ key: "Authorization", value: "Bearer {lms_api_key}" }],
          body: [],
          variables: [
            {
              name: "subscriptions[0].student_email",
              customName: "student_email",
              type: "string",
            },
            {
              name: "subscriptions[0].student_name",
              customName: "student_name",
              type: "string",
            },
            {
              name: "subscriptions[0].course_name",
              customName: "course_name",
              type: "string",
            },
            {
              name: "subscriptions[0].expiry_date",
              customName: "expiry_date",
              type: "string",
            },
            {
              name: "subscriptions[0].subscription_id",
              customName: "subscription_id",
              type: "string",
            },
            {
              name: "subscriptions[0].renewal_price",
              customName: "renewal_price",
              type: "string",
            },
            { name: "total", customName: "expiring_count", type: "number" },
          ],
        },
      }),

      // 3. Condition: Any expiring subscriptions?
      n("3", "condition", "Any subscriptions expiring?", 520, {
        conditionData: [
          {
            variable: "expiring_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI: Generate personalized renewal pitch
      aiNode(
        "4",
        "AI: Generate renewal offer",
        760,
        "You are a course renewal advisor. Based on the student's course and progress, create a compelling renewal message. Highlight what they'll lose, what's coming next, and offer an incentive. Respond JSON: {renewal_subject, renewal_body, incentive_offer, urgency_message}",
        "Student: {student_name}, Course: {course_name}, Expiry: {expiry_date}, Price: {renewal_price}",
        [
          { field: "renewal_subject", as: "renewal_subject", type: "string" },
          { field: "renewal_body", as: "renewal_body", type: "string" },
          { field: "incentive_offer", as: "incentive_offer", type: "string" },
          { field: "urgency_message", as: "urgency_message", type: "string" },
        ],
      ),

      // 5. Send renewal email
      emailNode(
        "5",
        "Send renewal email",
        1000,
        "{student_email}",
        "{renewal_subject}",
        "{renewal_body}<br><br><strong>Special Offer:</strong> {incentive_offer}<br><em>{urgency_message}</em><br><br><a href='{renewal_url}'>Renew Now for {renewal_price}</a>",
      ),

      // 6. Send push notification
      n("6", "pushNotification", "Push: Subscription expiring soon", 1240, {
        pushNotificationDetails: {
          title: "Your {course_name} access expires soon",
          body: "{urgency_message} Renew now and keep learning.",
          url: "{renewal_url}",
        },
      }),

      // 7. Log renewal outreach
      n("7", "hitApi", "Log renewal outreach in LMS", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourlms.com/v1/subscriptions/{subscription_id}/renewal-outreach",
          headers: [
            { key: "Authorization", value: "Bearer {lms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "notification_type", value: "renewal_reminder" },
            { key: "channels", value: '["email","push"]' },
            { key: "incentive_offered", value: "{incentive_offer}" },
          ],
          variables: [
            { name: "logged", customName: "outreach_logged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has expiring -> generate renewal
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Course Subscription Renewal** — Daily check for expiring course subscriptions with AI-generated renewal offers and multi-channel reminders.\n\n1. Runs daily on schedule\n2. Fetches subscriptions expiring within 14 days\n3. AI generates personalized renewal pitch with incentive\n4. Sends renewal email with special offer\n5. Follows up with push notification\n6. Logs outreach in LMS for tracking\n\n**To activate:** Configure your LMS API endpoint and push notification service.`,
  },

  // ===================================================================
  // 114. CUSTOM GPT KNOWLEDGE WORKER (8 nodes)
  // ===================================================================

  "Custom GPT Knowledge Worker": {
    nodes: [
      trigger("Webhook: Knowledge query submitted", "webhook_form", {
        source: "Internal Portal / Slack Bot",
        event: "knowledge.query",
        howItStarts:
          "A team member submits a question to the internal knowledge worker bot via portal or Slack",
        setup:
          "Connect your internal portal form or Slack slash command to POST queries to this workflow URL. Include the question, requester_email, and context/department.",
        inputFields: [],
      }),

      // 2. AI: Parse and classify the query
      aiNode(
        "2",
        "AI: Classify query and extract intent",
        280,
        "You are a knowledge management query classifier. Parse the user's question, identify the knowledge domain (engineering, sales, hr, legal, product, finance), and determine if this needs a factual lookup, a process guide, or a policy clarification. Respond JSON: {requester_email, requester_name, question, domain, query_type, search_keywords, confidence}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "question", as: "question", type: "string" },
          { field: "domain", as: "domain", type: "string" },
          { field: "query_type", as: "query_type", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
          {
            field: "confidence",
            as: "classification_confidence",
            type: "number",
          },
        ],
      ),

      // 3. Search internal knowledge base
      n("3", "hitApi", "Search internal knowledge base", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/knowledge/search",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query", value: "{search_keywords}" },
            { key: "domain", value: "{domain}" },
            { key: "limit", value: "5" },
          ],
          variables: [
            {
              name: "results[0].title",
              customName: "top_result_title",
              type: "string",
            },
            {
              name: "results[0].content",
              customName: "top_result_content",
              type: "string",
            },
            {
              name: "results[0].url",
              customName: "top_result_url",
              type: "string",
            },
            {
              name: "total_results",
              customName: "total_results",
              type: "number",
            },
            {
              name: "results_summary",
              customName: "results_summary",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI: Generate comprehensive answer from knowledge base results
      aiNode(
        "4",
        "AI: Generate answer from knowledge base",
        760,
        "You are an expert knowledge worker. Using the search results from the internal knowledge base, provide a comprehensive, accurate answer to the user's question. Cite sources. If the knowledge base doesn't have the answer, say so clearly. Respond JSON: {answer, sources_cited, confidence_score, needs_human_review, suggested_followup}",
        "Question: {question}\\nDomain: {domain}\\nQuery type: {query_type}\\nKnowledge base results: {results_summary}\\nTop result: {top_result_title} - {top_result_content}",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
          {
            field: "confidence_score",
            as: "answer_confidence",
            type: "number",
          },
          {
            field: "needs_human_review",
            as: "needs_human_review",
            type: "string",
          },
          {
            field: "suggested_followup",
            as: "suggested_followup",
            type: "string",
          },
        ],
      ),

      // 5. Condition: High confidence answer?
      n("5", "condition", "High confidence answer (> 75)?", 1000, {
        conditionData: [
          {
            variable: "answer_confidence",
            value: "75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. High confidence: Send answer directly
      emailNode(
        "6",
        "Send answer to requester",
        1240,
        "{requester_email}",
        "Answer: {question}",
        "Hi {requester_name},\n\nHere's the answer to your question:\n\n{generated_answer}\n\nSources: {sources_cited}\n\nSuggested follow-up: {suggested_followup}\n\nWasn't helpful? Reply and a human expert will follow up.",
        380,
      ),

      // 7. Low confidence: Route to human expert for approval
      n(
        "7",
        "approvalGate",
        "Human expert review required",
        1240,
        {
          approvalGateData: {
            message:
              "Low-confidence knowledge answer needs review.\n\nQuestion: {question}\nDomain: {domain}\nGenerated answer: {generated_answer}\nConfidence: {answer_confidence}%\nSources: {sources_cited}\n\nApprove to send as-is, or reject to manually respond.",
          },
        },
        -220,
      ),

      // 8. Log query and response for knowledge improvement
      n("8", "hitApi", "Log query for knowledge improvement", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/knowledge/query-log",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "question", value: "{question}" },
            { key: "domain", value: "{domain}" },
            { key: "answer", value: "{generated_answer}" },
            { key: "confidence", value: "{answer_confidence}" },
            { key: "sources", value: "{sources_cited}" },
            { key: "requester", value: "{requester_email}" },
          ],
          variables: [
            { name: "log_id", customName: "query_log_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high confidence -> send directly
      e("5", "7", "output_2"), // low confidence -> human review
      e("6", "8"),
      e("7", "8"),
    ],
    summary: `**Custom GPT Knowledge Worker** — Internal AI knowledge bot that searches company knowledge base, generates answers with citations, and routes low-confidence queries to human experts.\n\n1. Fires on knowledge query from internal portal or Slack\n2. AI classifies query domain and intent\n3. Searches internal knowledge base with semantic keywords\n4. AI generates comprehensive answer with source citations\n5. High confidence: sends answer directly to requester\n6. Low confidence: routes to human expert for approval\n7. Logs all queries for continuous knowledge improvement\n\n**To activate:** Connect your internal portal, knowledge base API, and Slack.`,
  },

  // ===================================================================
  // 115. CUSTOMER EDUCATION DRIP FOR NEW ACCOUNT (7 nodes)
  // ===================================================================

  "Customer Education Drip for New Account": {
    nodes: [
      trigger("App webhook: New account activated", "webhook_app", {
        source: "App Backend",
        event: "account.activated",
        howItStarts:
          "A new customer account is activated and needs a structured education series over their first 14 days",
        setup:
          "Configure your app backend to POST when a new account is activated. Include account_id, primary_email, company_name, plan, and industry.",
        inputFields: [],
      }),

      // 2. Send Day 0: Welcome + getting started
      emailNode(
        "2",
        "Day 0: Welcome + getting started guide",
        280,
        "{primary_email}",
        "Welcome to the platform — Your getting started guide",
        "Hi {company_name} team,\n\nWelcome aboard! Here's everything you need to get started in your first 24 hours:\n\n1. Complete your workspace setup\n2. Invite your team members\n3. Connect your first integration\n\nQuickstart guide: {quickstart_url}\n\nWe'll send you a series of tips over the next 2 weeks to help you get the most value.",
      ),

      // 3. Delay 3 days
      n("3", "delayAction", "Wait 3 days", 520, {
        delayData: { delay: 3, delayType: "days" },
      }),

      // 4. Send Day 3: Core features walkthrough
      emailNode(
        "4",
        "Day 3: Core features walkthrough",
        760,
        "{primary_email}",
        "3 features every new team should set up this week",
        "Hi {company_name} team,\n\nHere are 3 features that our most successful customers set up in their first week:\n\n1. Automated workflows — save 10+ hours/week\n2. Team dashboards — keep everyone aligned\n3. Integration hub — connect your existing tools\n\nStep-by-step tutorials: {tutorials_url}",
      ),

      // 5. Delay 4 more days (Day 7)
      n("5", "delayAction", "Wait 4 more days (Day 7)", 1000, {
        delayData: { delay: 4, delayType: "days" },
      }),

      // 6. Send Day 7: Advanced tips + case study
      emailNode(
        "6",
        "Day 7: Advanced tips + success story",
        1240,
        "{primary_email}",
        "How {industry_peer} got 3x ROI in their first month",
        "Hi {company_name} team,\n\nYou're one week in! Here's how a company in {industry} used these advanced features to get 3x ROI:\n\n- Custom automations for {industry}-specific workflows\n- Advanced analytics to track what matters\n- API integrations for seamless data flow\n\nRead the case study: {case_study_url}\nBook a strategy call: {strategy_call_url}",
      ),

      // 7. Track education drip completion
      n("7", "hitApi", "Track education drip progress", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/accounts/{account_id}/education-tracking",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "drip_name", value: "new_account_education" },
            { key: "emails_sent", value: "3" },
            { key: "last_email", value: "day_7_advanced_tips" },
            { key: "completed", value: "true" },
          ],
          variables: [
            { name: "tracking_id", customName: "tracking_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Customer Education Drip for New Account** — Structured 7-day education series that teaches new accounts core features, advanced tips, and shares industry-relevant case studies.\n\n1. Fires when new account is activated\n2. Day 0: Welcome email with quickstart guide\n3. Day 3: Core features walkthrough with tutorials\n4. Day 7: Advanced tips with industry-specific case study\n5. Tracks drip completion for lifecycle management\n\n**To activate:** Connect your app backend webhook.`,
  },

  // ===================================================================
  // 116. DAILY REPORT GENERATOR (8 nodes)
  // ===================================================================

  "Daily Report Generator": {
    nodes: [
      trigger("Schedule: Daily report at 7 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Automatically generates a daily business report every morning with key metrics from multiple sources",
        setup:
          "This workflow runs automatically on schedule. Configure API keys for your analytics, CRM, and finance platforms.",
        inputFields: [],
      }),

      // 2. Fetch analytics metrics
      n("2", "hitApi", "Fetch analytics metrics (Google Analytics)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"yesterday","endDate":"yesterday"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"activeUsers"},{"name":"sessions"},{"name":"conversions"},{"name":"totalRevenue"}]',
            },
          ],
          variables: [
            {
              name: "rows[0].metricValues[0].value",
              customName: "daily_active_users",
              type: "number",
            },
            {
              name: "rows[0].metricValues[1].value",
              customName: "daily_sessions",
              type: "number",
            },
            {
              name: "rows[0].metricValues[2].value",
              customName: "daily_conversions",
              type: "number",
            },
            {
              name: "rows[0].metricValues[3].value",
              customName: "daily_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 3. Fetch CRM pipeline metrics
      n("3", "hitApi", "Fetch CRM pipeline data (HubSpot)", 520, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.hubapi.com/crm/v3/objects/deals?limit=100&properties=amount,dealstage,createdate&filterGroups=[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"{yesterday_epoch}"}]}]',
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            { name: "total", customName: "new_deals_count", type: "number" },
            {
              name: "paging.next.after",
              customName: "pipeline_value",
              type: "string",
            },
          ],
        },
      }),

      // 4. Fetch support ticket stats
      n("4", "hitApi", "Fetch support metrics (Zendesk)", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket created>yesterday",
          headers: [{ key: "Authorization", value: "Basic {zendesk_auth}" }],
          body: [],
          variables: [
            { name: "count", customName: "new_tickets_count", type: "number" },
            {
              name: "results[0].satisfaction_rating.score",
              customName: "avg_csat",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI: Compile daily report with insights
      aiNode(
        "5",
        "AI: Compile daily report with insights",
        1000,
        "You are a business analyst. Compile a concise daily report from these metrics. Highlight trends, anomalies, and recommended actions. Format as HTML with sections for Traffic, Sales, and Support. Respond JSON: {report_html, executive_summary, top_insight, action_items, overall_health_score}",
        "Analytics: {daily_active_users} users, {daily_sessions} sessions, {daily_conversions} conversions, ${daily_revenue} revenue. CRM: {new_deals_count} new deals. Support: {new_tickets_count} new tickets, CSAT: {avg_csat}.",
        [
          { field: "report_html", as: "report_html", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "top_insight", as: "top_insight", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "overall_health_score", as: "health_score", type: "number" },
        ],
      ),

      // 6. Send report via email
      emailNode(
        "6",
        "Send daily report email",
        1240,
        "{report_recipients}",
        "Daily Report — Health Score: {health_score}/100",
        "{report_html}",
      ),

      // 7. Post to Slack #daily-reports
      slackNode(
        "7",
        "Slack: Post daily report summary",
        1480,
        "Daily Report — Health: {health_score}/100\n\n{executive_summary}\n\nTop insight: {top_insight}\n\nAction items: {action_items}",
      ),

      // 8. Archive report to data warehouse
      n("8", "hitApi", "Archive report to data warehouse", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/reports/archive",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "report_type", value: "daily" },
            { key: "health_score", value: "{health_score}" },
            {
              key: "metrics",
              value:
                '{"users":"{daily_active_users}","sessions":"{daily_sessions}","conversions":"{daily_conversions}","revenue":"{daily_revenue}","deals":"{new_deals_count}","tickets":"{new_tickets_count}"}',
            },
            { key: "summary", value: "{executive_summary}" },
          ],
          variables: [
            { name: "archive_id", customName: "archive_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Daily Report Generator** — Pulls metrics from Analytics, CRM, and Support; AI compiles a daily business report with health score and insights.\n\n1. Runs daily at 7 AM on schedule\n2. Fetches GA4 analytics: users, sessions, conversions, revenue\n3. Fetches HubSpot pipeline: new deals\n4. Fetches Zendesk support: tickets, CSAT\n5. AI compiles report with executive summary, insights, and action items\n6. Emails report to stakeholders\n7. Posts summary to Slack #daily-reports\n8. Archives to data warehouse for trending\n\n**To activate:** Connect Google Analytics, HubSpot, Zendesk, Slack, and email.`,
  },

  // ===================================================================
  // 117. DEEP RESEARCH AGENT (10 nodes)
  // ===================================================================

  "Deep Research Agent": {
    nodes: [
      trigger("Manual: Submit research query", "manual", {
        howItStarts:
          "A team member submits a deep research question requiring multi-source analysis",
        setup:
          "Submit a research query via the workflow interface or POST to the API. Include the research question, context, and desired output format.",
        inputFields: [
          {
            name: "research_question",
            type: "string",
            example:
              "What are the emerging trends in AI-powered customer support for SaaS companies in 2025?",
          },
          {
            name: "context",
            type: "string",
            example: "We are a B2B SaaS company evaluating AI support tools",
          },
          {
            name: "output_format",
            type: "string",
            example: "executive briefing",
          },
        ],
      }),

      // 2. AI: Break down research into sub-queries
      aiNode(
        "2",
        "AI: Decompose into sub-queries",
        280,
        "You are a research strategist. Break down the research question into 3-5 focused sub-queries that can be independently researched. Each sub-query should target a specific aspect. Respond JSON: {sub_query_1, sub_query_2, sub_query_3, sub_query_4, research_scope, estimated_complexity}",
        "Research question: {research_question}. Context: {context}. Output format: {output_format}",
        [
          { field: "sub_query_1", as: "sub_query_1", type: "string" },
          { field: "sub_query_2", as: "sub_query_2", type: "string" },
          { field: "sub_query_3", as: "sub_query_3", type: "string" },
          { field: "sub_query_4", as: "sub_query_4", type: "string" },
          { field: "research_scope", as: "research_scope", type: "string" },
          {
            field: "estimated_complexity",
            as: "estimated_complexity",
            type: "string",
          },
        ],
      ),

      // 3. Web search: Sub-query 1
      n("3", "hitApi", "Web search: Sub-query 1 (Serper)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{sub_query_1}" },
            { key: "num", value: "10" },
          ],
          variables: [
            {
              name: "organic[0].title",
              customName: "search1_title",
              type: "string",
            },
            {
              name: "organic[0].snippet",
              customName: "search1_snippet",
              type: "string",
            },
            {
              name: "organic[0].link",
              customName: "search1_url",
              type: "string",
            },
            {
              name: "answerBox.answer",
              customName: "search1_answer",
              type: "string",
            },
          ],
        },
      }),

      // 4. Web search: Sub-query 2
      n("4", "hitApi", "Web search: Sub-query 2 (Serper)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{sub_query_2}" },
            { key: "num", value: "10" },
          ],
          variables: [
            {
              name: "organic[0].title",
              customName: "search2_title",
              type: "string",
            },
            {
              name: "organic[0].snippet",
              customName: "search2_snippet",
              type: "string",
            },
            {
              name: "organic[0].link",
              customName: "search2_url",
              type: "string",
            },
            {
              name: "answerBox.answer",
              customName: "search2_answer",
              type: "string",
            },
          ],
        },
      }),

      // 5. Web search: Sub-query 3
      n("5", "hitApi", "Web search: Sub-query 3 (Serper)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{sub_query_3}" },
            { key: "num", value: "10" },
          ],
          variables: [
            {
              name: "organic[0].title",
              customName: "search3_title",
              type: "string",
            },
            {
              name: "organic[0].snippet",
              customName: "search3_snippet",
              type: "string",
            },
            {
              name: "organic[0].link",
              customName: "search3_url",
              type: "string",
            },
            {
              name: "answerBox.answer",
              customName: "search3_answer",
              type: "string",
            },
          ],
        },
      }),

      // 6. Web search: Sub-query 4
      n("6", "hitApi", "Web search: Sub-query 4 (Serper)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{sub_query_4}" },
            { key: "num", value: "10" },
          ],
          variables: [
            {
              name: "organic[0].title",
              customName: "search4_title",
              type: "string",
            },
            {
              name: "organic[0].snippet",
              customName: "search4_snippet",
              type: "string",
            },
            {
              name: "organic[0].link",
              customName: "search4_url",
              type: "string",
            },
            {
              name: "answerBox.answer",
              customName: "search4_answer",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI: Synthesize all research into comprehensive report
      aiNode(
        "7",
        "AI: Synthesize research into report",
        1480,
        "You are a senior research analyst. Synthesize all the search results into a comprehensive research report. Cross-reference findings, identify patterns, note contradictions, and provide actionable conclusions. Format as the requested output type. Respond JSON: {report_html, executive_summary, key_findings, recommendations, confidence_level, sources_list}",
        "Original question: {research_question}\\nContext: {context}\\nFormat: {output_format}\\n\\nSearch 1 ({sub_query_1}): {search1_title} - {search1_snippet} ({search1_url}). Answer: {search1_answer}\\nSearch 2 ({sub_query_2}): {search2_title} - {search2_snippet} ({search2_url}). Answer: {search2_answer}\\nSearch 3 ({sub_query_3}): {search3_title} - {search3_snippet} ({search3_url}). Answer: {search3_answer}\\nSearch 4 ({sub_query_4}): {search4_title} - {search4_snippet} ({search4_url}). Answer: {search4_answer}",
        [
          { field: "report_html", as: "report_html", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_findings", as: "key_findings", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          { field: "sources_list", as: "sources_list", type: "string" },
        ],
      ),

      // 8. Approval gate: Review before distribution
      n("8", "approvalGate", "Review research report before sending", 1720, {
        approvalGateData: {
          message:
            "Deep research report ready for review.\n\nQuestion: {research_question}\nConfidence: {confidence_level}\n\nExecutive Summary: {executive_summary}\n\nKey Findings: {key_findings}\n\nSources: {sources_list}\n\nApprove to distribute, reject to revise.",
        },
      }),

      // 9. Email the research report
      emailNode(
        "9",
        "Email research report",
        1960,
        "{report_recipients}",
        "Research Report: {research_question}",
        "{report_html}<br><br><strong>Recommendations:</strong> {recommendations}<br><strong>Sources:</strong> {sources_list}",
      ),

      // 10. Post summary to Slack
      slackNode(
        "10",
        "Slack: Share research summary",
        2200,
        "Deep Research Complete: {research_question}\n\n{executive_summary}\n\nKey findings: {key_findings}\n\nFull report emailed to stakeholders. Confidence: {confidence_level}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
    summary: `**Deep Research Agent** — Multi-step AI research agent that decomposes questions, runs parallel web searches, synthesizes findings into a cited report, and requires human approval before distribution.\n\n1. Submit a research question with context\n2. AI decomposes into 4 focused sub-queries\n3. Runs 4 web searches via Serper API\n4. AI synthesizes all results into a comprehensive report with citations\n5. Human approval gate before distribution\n6. Emails full report to stakeholders\n7. Posts summary to Slack\n\n**To activate:** Connect OpenAI, Serper API, Slack, and email.`,
  },

  // ===================================================================
  // 118. DO-NOT-DISTURB SAFE-SEND WRAPPER (6 nodes)
  // ===================================================================

  "Do-Not-Disturb Safe-Send Wrapper": {
    nodes: [
      trigger("Webhook: Outbound message request", "webhook_app", {
        source: "Internal Systems",
        event: "message.send_request",
        howItStarts:
          "An internal system requests to send a message (email, SMS, push) and this workflow checks DND status before allowing delivery",
        setup:
          "Route all outbound messaging requests through this workflow URL. Include recipient_id, recipient_email, message_type, subject, body, and channel.",
        inputFields: [],
      }),

      // 2. AI: Parse message request
      aiNode(
        "2",
        "AI: Parse outbound message request",
        280,
        "You are a message routing parser. Extract the recipient, message content, and channel from the webhook payload. Respond JSON: {recipient_email, recipient_id, message_type, subject, body, channel, priority}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_id", as: "recipient_id", type: "string" },
          { field: "message_type", as: "message_type", type: "string" },
          { field: "subject", as: "subject", type: "string" },
          { field: "body", as: "body", type: "string" },
          { field: "channel", as: "channel", type: "string" },
          { field: "priority", as: "priority", type: "string" },
        ],
      ),

      // 3. Check user DND preferences and timezone
      n("3", "hitApi", "Check DND preferences and timezone", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{recipient_id}/communication-preferences",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            { name: "dnd_enabled", customName: "dnd_enabled", type: "boolean" },
            { name: "dnd_start", customName: "dnd_start", type: "string" },
            { name: "dnd_end", customName: "dnd_end", type: "string" },
            { name: "timezone", customName: "user_timezone", type: "string" },
            {
              name: "is_currently_dnd",
              customName: "is_currently_dnd",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. Condition: Is user currently in DND window?
      n("4", "condition", "Currently in DND window?", 760, {
        conditionData: [
          {
            variable: "is_currently_dnd",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. DND active: Queue message for delivery after DND ends
      n(
        "5",
        "runCode",
        "Queue message for post-DND delivery",
        1000,
        {
          runCodeDetails: {
            code: "// Calculate delay until DND window ends\nconst dndEnd = input.dnd_end; // e.g., '08:00'\nconst timezone = input.user_timezone;\nconst now = new Date();\n// Store message in queue for delivery at DND end time\noutput.queued = true;\noutput.delivery_time = dndEnd + ' ' + timezone;\noutput.queue_status = 'queued_for_dnd_end';\noutput.message_preserved = true;",
            outputVariables: [
              { name: "queued", type: "boolean" },
              { name: "delivery_time", type: "string" },
              { name: "queue_status", type: "string" },
            ],
          },
        },
        380,
      ),

      // 6. Not DND: Send immediately
      emailNode(
        "6",
        "Send message immediately",
        1000,
        "{recipient_email}",
        "{subject}",
        "{body}",
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // DND active -> queue for later
      e("4", "6", "output_2"), // Not DND -> send now
    ],
    summary: `**Do-Not-Disturb Safe-Send Wrapper** — Wraps all outbound messaging to check recipient DND preferences before delivery, queuing messages for later if in DND window.\n\n1. Intercepts outbound message requests from internal systems\n2. AI parses recipient, content, and channel\n3. Checks user DND preferences and current timezone status\n4. If in DND window: queues message for delivery after DND ends\n5. If not in DND: sends message immediately\n\n**To activate:** Route outbound messages through this workflow and configure your user preferences API.`,
  },

  // ===================================================================
  // 119. DORMANT DAY-3 ONBOARDING RESCUE (7 nodes)
  // ===================================================================

  "Dormant Day-3 Onboarding Rescue": {
    nodes: [
      trigger("Schedule: Daily check for Day-3 dormant users", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Daily scan for users who signed up 3 days ago but have not completed onboarding or taken any key action",
        setup:
          "This workflow runs automatically. Configure your app API to return users who signed up 3 days ago with zero key actions.",
        inputFields: [],
      }),

      // 2. Fetch Day-3 dormant users
      n("2", "hitApi", "Fetch Day-3 dormant users", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users?signed_up_days_ago=3&key_actions=0&status=active",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "users[0].email",
              customName: "user_email",
              type: "string",
            },
            { name: "users[0].name", customName: "user_name", type: "string" },
            { name: "users[0].id", customName: "user_id", type: "string" },
            {
              name: "users[0].signup_source",
              customName: "signup_source",
              type: "string",
            },
            { name: "total", customName: "dormant_count", type: "number" },
          ],
        },
      }),

      // 3. Condition: Any dormant users found?
      n("3", "condition", "Any Day-3 dormant users?", 520, {
        conditionData: [
          {
            variable: "dormant_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI: Generate personalized re-engagement
      aiNode(
        "4",
        "AI: Generate Day-3 rescue message",
        760,
        "You are an onboarding specialist. Write a warm, helpful Day-3 rescue email for a user who signed up but hasn't taken action. Focus on removing friction: offer help, suggest the easiest first step, and include a direct CTA. Respond JSON: {subject_line, email_body, suggested_first_step, help_offer}",
        "User: {user_name}, Email: {user_email}, Source: {signup_source}, Days since signup: 3, Key actions: 0",
        [
          { field: "subject_line", as: "rescue_subject", type: "string" },
          { field: "email_body", as: "rescue_body", type: "string" },
          { field: "suggested_first_step", as: "first_step", type: "string" },
          { field: "help_offer", as: "help_offer", type: "string" },
        ],
      ),

      // 5. Send rescue email
      emailNode(
        "5",
        "Send Day-3 rescue email",
        1000,
        "{user_email}",
        "{rescue_subject}",
        "{rescue_body}<br><br><strong>Your easiest first step:</strong> {first_step}<br><br>{help_offer}",
      ),

      // 6. Send push notification
      n("6", "pushNotification", "Push: Day-3 nudge", 1240, {
        pushNotificationDetails: {
          title: "Need help getting started?",
          body: "You signed up 3 days ago but haven't tried {first_step} yet. It takes 2 minutes!",
          url: "{onboarding_url}",
        },
      }),

      // 7. Tag user as at-risk in app
      n("7", "hitApi", "Tag user as at-risk in app", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.yourapp.com/v1/users/{user_id}",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "tags", value: '["day3_dormant","at_risk","rescue_sent"]' },
            { key: "lifecycle_stage", value: "at_risk_onboarding" },
          ],
          variables: [
            { name: "updated", customName: "user_tagged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // dormant users found -> generate rescue
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Dormant Day-3 Onboarding Rescue** — Identifies users who signed up 3 days ago with zero key actions and sends personalized rescue outreach.\n\n1. Runs daily to find Day-3 dormant users\n2. AI generates personalized rescue email with friction-reducing first step\n3. Sends rescue email with help offer\n4. Follows up with push notification\n5. Tags user as at-risk in app for lifecycle tracking\n\n**To activate:** Configure your app API to expose dormant user queries.`,
  },

  // ===================================================================
  // 120. DORMANT DAY-7 ONBOARDING RESCUE (8 nodes)
  // ===================================================================

  "Dormant Day-7 Onboarding Rescue": {
    nodes: [
      trigger("Schedule: Daily check for Day-7 dormant users", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Daily scan for users who signed up 7 days ago, received Day-3 rescue, but still have not completed onboarding",
        setup:
          "This workflow runs automatically. Configure your app API to return users signed up 7 days ago with the day3_dormant tag still active.",
        inputFields: [],
      }),

      // 2. Fetch Day-7 dormant users
      n("2", "hitApi", "Fetch Day-7 dormant users", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users?signed_up_days_ago=7&key_actions=0&tags=day3_dormant&status=active",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "users[0].email",
              customName: "user_email",
              type: "string",
            },
            { name: "users[0].name", customName: "user_name", type: "string" },
            { name: "users[0].id", customName: "user_id", type: "string" },
            {
              name: "users[0].signup_source",
              customName: "signup_source",
              type: "string",
            },
            { name: "total", customName: "dormant_count", type: "number" },
          ],
        },
      }),

      // 3. Condition: Any Day-7 dormant users?
      n("3", "condition", "Any Day-7 dormant users?", 520, {
        conditionData: [
          {
            variable: "dormant_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI: Generate more urgent rescue with social proof
      aiNode(
        "4",
        "AI: Generate Day-7 escalated rescue",
        760,
        "You are an onboarding specialist handling escalated dormant users. This user signed up 7 days ago and ignored the Day-3 rescue email. Write a more urgent but still empathetic message. Include social proof, offer a live demo, and create mild FOMO. Respond JSON: {subject_line, email_body, social_proof, demo_cta, fomo_line}",
        "User: {user_name}, Email: {user_email}, Source: {signup_source}, Days since signup: 7, Key actions: 0, Previous rescue: Day-3 email sent and ignored",
        [
          { field: "subject_line", as: "rescue_subject", type: "string" },
          { field: "email_body", as: "rescue_body", type: "string" },
          { field: "social_proof", as: "social_proof", type: "string" },
          { field: "demo_cta", as: "demo_cta", type: "string" },
          { field: "fomo_line", as: "fomo_line", type: "string" },
        ],
      ),

      // 5. Send escalated rescue email
      emailNode(
        "5",
        "Send Day-7 escalated rescue email",
        1000,
        "{user_email}",
        "{rescue_subject}",
        "{rescue_body}<br><br>{social_proof}<br><br>{demo_cta}<br><br><em>{fomo_line}</em>",
      ),

      // 6. Send SMS as additional channel
      n("6", "sendSMS", "SMS: Day-7 rescue nudge", 1240, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "Hi {user_name}, your free trial is ticking. Need help getting started? Reply YES for a quick walkthrough.",
        },
      }),

      // 7. Escalate to CSM via Slack
      slackNode(
        "7",
        "Slack: Escalate to CSM",
        1480,
        "Day-7 dormant user escalation: {user_name} ({user_email}) signed up 7 days ago with zero actions. Day-3 rescue failed. Source: {signup_source}. Manual outreach recommended.",
      ),

      // 8. Update lifecycle stage to critical
      n("8", "hitApi", "Update lifecycle: critical churn risk", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.yourapp.com/v1/users/{user_id}",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "tags",
              value: '["day7_dormant","critical_churn_risk","csm_escalated"]',
            },
            { key: "lifecycle_stage", value: "critical_churn_risk" },
          ],
          variables: [
            { name: "updated", customName: "user_updated", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // dormant users found -> escalated rescue
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Dormant Day-7 Onboarding Rescue** — Escalated rescue for users who ignored Day-3 outreach and remain dormant at Day 7.\n\n1. Runs daily to find Day-7 dormant users who already received Day-3 rescue\n2. AI generates escalated rescue with social proof and demo offer\n3. Sends multi-channel outreach: email + SMS\n4. Escalates to CSM via Slack for manual outreach\n5. Tags user as critical churn risk\n\n**To activate:** Configure your app API and ensure Day-3 rescue tags users correctly.`,
  },

  // ===================================================================
  // 121. EDTECH COURSE ENROLL NO LESSON START (6 nodes)
  // ===================================================================

  "EdTech Course Enroll No Lesson Start": {
    nodes: [
      trigger("LMS webhook: Student enrolled in course", "webhook_app", {
        source: "LMS / EdTech Platform",
        event: "enrollment.created",
        howItStarts:
          "A student enrolls in a course but has not started their first lesson within 48 hours",
        setup:
          "Configure your LMS to POST enrollment events to this workflow URL. Include student_id, email, course_id, course_name, and enrollment_date.",
        inputFields: [],
      }),

      // 2. Wait 48 hours for lesson start
      n("2", "delayAction", "Wait 48h for first lesson start", 280, {
        delayData: { delay: 48, delayType: "hours" },
      }),

      // 3. Check if any lesson was started
      n("3", "hitApi", "Check lesson progress", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourlms.com/v1/enrollments/{enrollment_id}/progress",
          headers: [{ key: "Authorization", value: "Bearer {lms_api_key}" }],
          body: [],
          variables: [
            {
              name: "lessons_started",
              customName: "lessons_started",
              type: "number",
            },
            {
              name: "first_lesson_title",
              customName: "first_lesson_title",
              type: "string",
            },
            {
              name: "first_lesson_url",
              customName: "first_lesson_url",
              type: "string",
            },
            {
              name: "student_email",
              customName: "student_email",
              type: "string",
            },
            {
              name: "student_name",
              customName: "student_name",
              type: "string",
            },
            { name: "course_name", customName: "course_name", type: "string" },
          ],
        },
      }),

      // 4. Condition: No lessons started?
      n("4", "condition", "Still zero lessons started?", 760, {
        conditionData: [
          {
            variable: "lessons_started",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send lesson start nudge
      emailNode(
        "5",
        "Send first lesson nudge",
        1000,
        "{student_email}",
        "{student_name}, your first lesson in {course_name} is waiting!",
        "Hi {student_name},\n\nYou enrolled in {course_name} 2 days ago but haven't started your first lesson yet. The hardest part is pressing play!\n\nYour first lesson: {first_lesson_title}\nStart now (takes 10 min): {first_lesson_url}\n\nStudents who start within the first 48 hours are 3x more likely to finish the course.",
      ),

      // 6. Push notification: lesson nudge
      n("6", "pushNotification", "Push: Start your first lesson", 1240, {
        pushNotificationDetails: {
          title: "Ready to start {course_name}?",
          body: "Your first lesson '{first_lesson_title}' takes just 10 minutes. Tap to begin!",
          url: "{first_lesson_url}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // no lessons started -> nudge
      e("5", "6"),
    ],
    summary: `**EdTech Course Enroll No Lesson Start** — Nudges students who enrolled in a course but haven't started their first lesson within 48 hours.\n\n1. Fires on course enrollment from LMS\n2. Waits 48 hours for organic lesson start\n3. Checks lesson progress via LMS API\n4. If zero lessons started, sends personalized nudge email\n5. Follows up with push notification linking directly to first lesson\n\n**To activate:** Connect your LMS webhook and push notification service.`,
  },

  // ===================================================================
  // 122. EDTECH LESSON START NO LESSON 2 (6 nodes)
  // ===================================================================

  "EdTech Lesson Start No Lesson 2": {
    nodes: [
      trigger("LMS webhook: Student completed lesson 1", "webhook_app", {
        source: "LMS / EdTech Platform",
        event: "lesson.completed",
        howItStarts:
          "A student completes lesson 1 of a course but doesn't start lesson 2 within 72 hours",
        setup:
          "Configure your LMS to POST lesson completion events to this workflow URL. Include student_id, email, course_id, lesson_number, and completion_timestamp.",
        inputFields: [],
      }),

      // 2. Wait 72 hours for lesson 2 start
      n("2", "delayAction", "Wait 72h for lesson 2 start", 280, {
        delayData: { delay: 72, delayType: "hours" },
      }),

      // 3. Check lesson 2 progress
      n("3", "hitApi", "Check lesson 2 progress", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourlms.com/v1/enrollments/{enrollment_id}/progress",
          headers: [{ key: "Authorization", value: "Bearer {lms_api_key}" }],
          body: [],
          variables: [
            {
              name: "lessons_completed",
              customName: "lessons_completed",
              type: "number",
            },
            {
              name: "next_lesson_title",
              customName: "next_lesson_title",
              type: "string",
            },
            {
              name: "next_lesson_url",
              customName: "next_lesson_url",
              type: "string",
            },
            {
              name: "student_email",
              customName: "student_email",
              type: "string",
            },
            {
              name: "student_name",
              customName: "student_name",
              type: "string",
            },
            { name: "course_name", customName: "course_name", type: "string" },
          ],
        },
      }),

      // 4. Condition: Still on lesson 1?
      n("4", "condition", "Still only 1 lesson completed?", 760, {
        conditionData: [
          {
            variable: "lessons_completed",
            value: "1",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send lesson 2 nudge with recap
      emailNode(
        "5",
        "Send lesson 2 continuation nudge",
        1000,
        "{student_email}",
        "Nice work on lesson 1, {student_name}! Lesson 2 builds on what you learned",
        "Hi {student_name},\n\nGreat job finishing lesson 1 of {course_name}! You're building momentum — don't let it fade.\n\nLesson 2: {next_lesson_title}\nContinue now: {next_lesson_url}\n\nFun fact: Students who complete lesson 2 within a week of lesson 1 have a 78% course completion rate. Keep going!",
      ),

      // 6. Push notification: continue learning
      n("6", "pushNotification", "Push: Continue to lesson 2", 1240, {
        pushNotificationDetails: {
          title: "Keep your streak alive!",
          body: "Lesson 2 of {course_name}: '{next_lesson_title}' is ready. Continue learning!",
          url: "{next_lesson_url}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // still on lesson 1 -> nudge
      e("5", "6"),
    ],
    summary: `**EdTech Lesson Start No Lesson 2** — Nudges students who completed lesson 1 but haven't started lesson 2 within 72 hours.\n\n1. Fires when student completes lesson 1\n2. Waits 72 hours for lesson 2 start\n3. Checks progress via LMS API\n4. If still on lesson 1, sends continuation nudge with recap\n5. Follows up with push notification\n\n**To activate:** Connect your LMS webhook.`,
  },

  // ===================================================================
  // 123. FEATURE ADOPTION COLLABORATION TEAMMATE INVITE (7 nodes)
  // ===================================================================

  "Feature Adoption Collaboration Teammate Invite": {
    nodes: [
      trigger(
        "App webhook: User active but no teammates invited",
        "webhook_app",
        {
          source: "App Backend",
          event: "user.activation_milestone",
          howItStarts:
            "A user reaches an activation milestone (e.g., 5+ sessions, 3+ projects created) but has not invited any teammates",
          setup:
            "Configure your app backend to POST when a user hits activation milestones without team invites. Include user_id, email, sessions_count, projects_count, and team_size.",
          inputFields: [],
        },
      ),

      // 2. AI: Parse activation data
      aiNode(
        "2",
        "AI: Analyze activation without collaboration",
        280,
        "You are a product adoption analyst. The user is active but hasn't invited teammates. Analyze their usage pattern and craft a collaboration pitch. Respond JSON: {user_email, user_name, user_id, sessions_count, projects_count, team_size, collaboration_pitch, feature_highlight}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "sessions_count", as: "sessions_count", type: "string" },
          { field: "projects_count", as: "projects_count", type: "string" },
          {
            field: "collaboration_pitch",
            as: "collaboration_pitch",
            type: "string",
          },
          {
            field: "feature_highlight",
            as: "feature_highlight",
            type: "string",
          },
        ],
      ),

      // 3. Send teammate invite nudge email
      emailNode(
        "3",
        "Send teammate invite nudge",
        520,
        "{user_email}",
        "Teams using our platform together see 3x the results, {user_name}",
        "Hi {user_name},\n\nYou've been crushing it — {sessions_count} sessions and {projects_count} projects created! But did you know teams that collaborate on our platform see 3x better results?\n\n{collaboration_pitch}\n\nInvite your first teammate: {invite_url}\n\nFeature spotlight: {feature_highlight}",
      ),

      // 4. Wait 5 days
      n("4", "delayAction", "Wait 5 days", 760, {
        delayData: { delay: 5, delayType: "days" },
      }),

      // 5. Check if teammate was invited
      n("5", "hitApi", "Check team invite status", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/team",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "team_size",
              customName: "current_team_size",
              type: "number",
            },
            {
              name: "pending_invites",
              customName: "pending_invites",
              type: "number",
            },
          ],
        },
      }),

      // 6. Condition: Still solo?
      n("6", "condition", "Still no teammates or invites?", 1240, {
        conditionData: [
          {
            variable: "current_team_size",
            value: "1",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
          {
            variable: "pending_invites",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Send follow-up with incentive
      emailNode(
        "7",
        "Send invite incentive email",
        1480,
        "{user_email}",
        "Invite a teammate this week and unlock a bonus, {user_name}",
        "Hi {user_name},\n\nWe know collaboration makes everything better. Invite a teammate this week and you'll both get an extra month of Pro features.\n\nInvite now: {invite_url}\n\nThis offer expires in 7 days.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // still solo -> incentive email
    ],
    summary: `**Feature Adoption Collaboration Teammate Invite** — Drives team adoption by nudging active solo users to invite teammates.\n\n1. Fires when an active user reaches milestones without inviting teammates\n2. AI analyzes usage pattern and crafts collaboration pitch\n3. Sends teammate invite nudge with social proof\n4. Waits 5 days and checks team invite status\n5. If still solo, sends follow-up with incentive offer\n\n**To activate:** Connect your app backend webhook.`,
  },

  // ===================================================================
  // 124. FEATURE ADOPTION CORE FEATURE A (7 nodes)
  // ===================================================================

  "Feature Adoption Core Feature A": {
    nodes: [
      trigger(
        "App webhook: User activated but hasn't used Feature A",
        "webhook_app",
        {
          source: "App Backend / Segment",
          event: "user.activated_no_feature_a",
          howItStarts:
            "A user has completed onboarding and used the app for 7+ days but has never used Core Feature A (e.g., Workflows/Automations)",
          setup:
            "Configure your app to POST when activated users haven't used Feature A after 7 days. Include user_id, email, days_since_signup, features_used, and plan_type.",
          inputFields: [],
        },
      ),

      // 2. AI: Analyze feature gap and generate nudge
      aiNode(
        "2",
        "AI: Analyze Feature A adoption gap",
        280,
        "You are a product adoption specialist. This activated user has never used Feature A (our core automation/workflow feature). Based on their current usage, explain why Feature A would benefit them specifically. Respond JSON: {user_email, user_name, user_id, current_features, feature_a_benefit, use_case_example, estimated_time_saved}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "current_features", as: "current_features", type: "string" },
          {
            field: "feature_a_benefit",
            as: "feature_a_benefit",
            type: "string",
          },
          { field: "use_case_example", as: "use_case_example", type: "string" },
          {
            field: "estimated_time_saved",
            as: "estimated_time_saved",
            type: "string",
          },
        ],
      ),

      // 3. Send Feature A discovery email
      emailNode(
        "3",
        "Send Feature A discovery email",
        520,
        "{user_email}",
        "You're missing our most powerful feature, {user_name}",
        "Hi {user_name},\n\nYou've been using {current_features} — great choices! But you haven't tried our Workflows feature yet, and here's why you should:\n\n{feature_a_benefit}\n\nExample: {use_case_example}\nEstimated time saved: {estimated_time_saved} per week\n\nTry it now: {feature_a_url}\nWatch the 2-min demo: {feature_a_demo_url}",
      ),

      // 4. Send in-app message
      n("4", "sendMessage", "In-app: Feature A tooltip", 760, {
        sendMessageDetails: {
          message:
            "Have you tried Workflows yet? Users like you save {estimated_time_saved}/week with automations. Click to explore.",
          type: "in_app",
        },
      }),

      // 5. Wait 4 days
      n("5", "delayAction", "Wait 4 days", 1000, {
        delayData: { delay: 4, delayType: "days" },
      }),

      // 6. Check Feature A usage
      n("6", "hitApi", "Check Feature A usage", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/feature-usage?feature=feature_a",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "times_used",
              customName: "feature_a_usage",
              type: "number",
            },
            {
              name: "first_used_at",
              customName: "feature_a_first_use",
              type: "string",
            },
          ],
        },
      }),

      // 7. Condition: Still hasn't used Feature A?
      n("7", "condition", "Still hasn't used Feature A?", 1480, {
        conditionData: [
          {
            variable: "feature_a_usage",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Feature Adoption Core Feature A** — Drives adoption of your core automation/workflow feature for activated users who haven't discovered it.\n\n1. Fires when an activated user hasn't used Feature A after 7 days\n2. AI generates personalized benefit messaging based on current usage\n3. Sends discovery email with use case and time-saved estimate\n4. Sends in-app tooltip message\n5. Waits 4 days and checks Feature A usage\n6. Branches based on adoption for further action\n\n**To activate:** Connect your app backend or Segment event tracking.`,
  },

  // ===================================================================
  // 125. FEATURE ADOPTION CORE FEATURE B (7 nodes)
  // ===================================================================

  "Feature Adoption Core Feature B": {
    nodes: [
      trigger(
        "App webhook: User activated but hasn't used Feature B",
        "webhook_app",
        {
          source: "App Backend / Segment",
          event: "user.activated_no_feature_b",
          howItStarts:
            "A user has completed onboarding and used Feature A but has not used Core Feature B (e.g., Analytics/Reports)",
          setup:
            "Configure your app to POST when activated users with Feature A adoption haven't used Feature B after 10 days. Include user_id, email, features_used, and feature_a_usage_count.",
          inputFields: [],
        },
      ),

      // 2. AI: Analyze Feature B adoption gap
      aiNode(
        "2",
        "AI: Analyze Feature B adoption gap",
        280,
        "You are a product adoption specialist. This user actively uses Feature A but has never tried Feature B (our analytics/reporting feature). Show how Feature B complements their Feature A usage. Respond JSON: {user_email, user_name, user_id, feature_a_usage_count, feature_b_benefit, synergy_example, impact_metric}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          {
            field: "feature_a_usage_count",
            as: "feature_a_usage_count",
            type: "string",
          },
          {
            field: "feature_b_benefit",
            as: "feature_b_benefit",
            type: "string",
          },
          { field: "synergy_example", as: "synergy_example", type: "string" },
          { field: "impact_metric", as: "impact_metric", type: "string" },
        ],
      ),

      // 3. Send Feature B discovery email
      emailNode(
        "3",
        "Send Feature B discovery email",
        520,
        "{user_email}",
        "You're using Workflows — now see the results with Analytics",
        "Hi {user_name},\n\nYou've used Workflows {feature_a_usage_count} times — impressive! But are you measuring the impact?\n\n{feature_b_benefit}\n\nHow it works together: {synergy_example}\nExpected impact: {impact_metric}\n\nTry Analytics now: {feature_b_url}\nSee a live demo: {feature_b_demo_url}",
      ),

      // 4. Send in-app message
      n("4", "sendMessage", "In-app: Feature B spotlight", 760, {
        sendMessageDetails: {
          message:
            "You've built {feature_a_usage_count} workflows! Want to see which ones perform best? Try Analytics to measure your impact.",
          type: "in_app",
        },
      }),

      // 5. Wait 5 days
      n("5", "delayAction", "Wait 5 days", 1000, {
        delayData: { delay: 5, delayType: "days" },
      }),

      // 6. Check Feature B usage
      n("6", "hitApi", "Check Feature B usage", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/feature-usage?feature=feature_b",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "times_used",
              customName: "feature_b_usage",
              type: "number",
            },
            {
              name: "first_used_at",
              customName: "feature_b_first_use",
              type: "string",
            },
          ],
        },
      }),

      // 7. Condition: Still hasn't used Feature B?
      n("7", "condition", "Still hasn't used Feature B?", 1480, {
        conditionData: [
          {
            variable: "feature_b_usage",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Feature Adoption Core Feature B** — Drives adoption of analytics/reporting (Feature B) for users already using workflows (Feature A).\n\n1. Fires when a Feature A user hasn't tried Feature B after 10 days\n2. AI generates synergy-focused messaging showing how B complements A\n3. Sends discovery email highlighting the A+B combination\n4. Sends in-app spotlight message\n5. Waits 5 days and checks Feature B usage\n6. Branches for further follow-up\n\n**To activate:** Connect your app backend or Segment event tracking.`,
  },

  // ===================================================================
  // 126. FEATURE REQUEST ACKNOWLEDGMENT (7 nodes)
  // ===================================================================

  "Feature Request Acknowledgment": {
    nodes: [
      trigger("Webhook: Feature request submitted", "webhook_form", {
        source: "In-App Form / Canny / Productboard",
        event: "feature_request.created",
        howItStarts:
          "A customer submits a feature request through in-app feedback, Canny, or a support channel",
        setup:
          "Connect your feature request form or tool (Canny, Productboard, Intercom) to POST to this workflow URL. Include requester_email, request_title, description, and priority.",
        inputFields: [],
      }),

      // 2. AI: Parse and categorize feature request
      aiNode(
        "2",
        "AI: Categorize feature request",
        280,
        "You are a product management assistant. Parse the feature request, categorize it (ux, performance, integration, new_feature, enhancement), estimate complexity (low/medium/high), and check if similar features exist on the roadmap. Respond JSON: {requester_email, requester_name, request_title, description, category, complexity, similar_existing, acknowledgment_message}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "request_title", as: "request_title", type: "string" },
          { field: "description", as: "request_description", type: "string" },
          { field: "category", as: "request_category", type: "string" },
          { field: "complexity", as: "estimated_complexity", type: "string" },
          { field: "similar_existing", as: "similar_existing", type: "string" },
          {
            field: "acknowledgment_message",
            as: "acknowledgment_message",
            type: "string",
          },
        ],
      ),

      // 3. Create feature request in tracking system
      n("3", "hitApi", "Create feature request in Productboard", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.productboard.com/notes",
          headers: [
            { key: "Authorization", value: "Bearer {productboard_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{request_title}" },
            { key: "content", value: "{request_description}" },
            { key: "customer_email", value: "{requester_email}" },
            {
              key: "tags",
              value: '["{request_category}","{estimated_complexity}"]',
            },
          ],
          variables: [
            {
              name: "data.id",
              customName: "feature_request_id",
              type: "string",
            },
            {
              name: "data.url",
              customName: "feature_request_url",
              type: "string",
            },
          ],
        },
      }),

      // 4. Send acknowledgment email
      emailNode(
        "4",
        "Send acknowledgment email to requester",
        760,
        "{requester_email}",
        "We received your feature request: {request_title}",
        "Hi {requester_name},\n\nThank you for your feature request!\n\n{acknowledgment_message}\n\nRequest: {request_title}\nCategory: {request_category}\nTracking ID: {feature_request_id}\n\nWe review all feature requests weekly and will update you on progress. Your feedback directly shapes our roadmap.",
      ),

      // 5. Approval gate: PM review for high-complexity requests
      n("5", "approvalGate", "PM review for high-complexity requests", 1000, {
        approvalGateData: {
          message:
            "New feature request needs PM review.\n\nTitle: {request_title}\nCategory: {request_category}\nComplexity: {estimated_complexity}\nDescription: {request_description}\nSimilar existing: {similar_existing}\nRequester: {requester_name} ({requester_email})\n\nApprove to add to backlog, reject to close with explanation.",
        },
      }),

      // 6. Slack: Notify product team
      slackNode(
        "6",
        "Slack: Notify product team",
        1240,
        "New feature request: *{request_title}*\nCategory: {request_category} | Complexity: {estimated_complexity}\nFrom: {requester_name} ({requester_email})\nSimilar: {similar_existing}\nProductboard: {feature_request_url}",
      ),

      // 7. Track in analytics
      n("7", "hitApi", "Track feature request in Segment", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event", value: "feature_request_submitted" },
            {
              key: "properties",
              value:
                '{"title":"{request_title}","category":"{request_category}","complexity":"{estimated_complexity}","requester":"{requester_email}"}',
            },
          ],
          variables: [
            { name: "success", customName: "tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Feature Request Acknowledgment** — Automatically categorizes feature requests, acknowledges the requester, logs in Productboard, and routes to PM for review.\n\n1. Fires when feature request is submitted\n2. AI categorizes request by type, complexity, and similarity to existing features\n3. Creates entry in Productboard\n4. Sends professional acknowledgment email with tracking ID\n5. PM approval gate for backlog prioritization\n6. Notifies product team on Slack\n7. Tracks analytics in Segment\n\n**To activate:** Connect your feedback tool, Productboard, Slack, and Segment.`,
  },

  // ===================================================================
  // 127. FINTECH KYC STARTED NOT COMPLETED (7 nodes)
  // ===================================================================

  "Fintech KYC Started Not Completed": {
    nodes: [
      trigger("App webhook: KYC verification started", "webhook_app", {
        source: "App Backend / KYC Provider",
        event: "kyc.started",
        howItStarts:
          "A fintech user starts KYC (Know Your Customer) identity verification but does not complete it within 24 hours",
        setup:
          "Configure your app or KYC provider (Onfido, Jumio, Persona) to POST when KYC starts. Include user_id, email, kyc_session_id, and step_reached.",
        inputFields: [],
      }),

      // 2. Wait 24 hours for KYC completion
      n("2", "delayAction", "Wait 24h for KYC completion", 280, {
        delayData: { delay: 24, delayType: "hours" },
      }),

      // 3. Check KYC completion status
      n("3", "hitApi", "Check KYC completion status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/kyc-status",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            { name: "status", customName: "kyc_status", type: "string" },
            {
              name: "step_reached",
              customName: "step_reached",
              type: "string",
            },
            {
              name: "blocking_reason",
              customName: "blocking_reason",
              type: "string",
            },
            { name: "user_email", customName: "user_email", type: "string" },
            { name: "user_name", customName: "user_name", type: "string" },
          ],
        },
      }),

      // 4. Condition: KYC still incomplete?
      n("4", "condition", "KYC still incomplete?", 760, {
        conditionData: [
          {
            variable: "kyc_status",
            value: "completed",
            comparison_operator: "NOT EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send KYC completion reminder with help
      emailNode(
        "5",
        "Send KYC completion reminder",
        1000,
        "{user_email}",
        "Complete your identity verification to unlock all features",
        "Hi {user_name},\n\nYou started your identity verification but didn't finish. You stopped at: {step_reached}.\n\nCompleting KYC unlocks:\n- Send and receive money\n- Full account access\n- Higher transaction limits\n\nIt takes about 3 minutes. Resume now: {kyc_resume_url}\n\nHaving trouble? Common issues:\n- Make sure your ID photo is clear and well-lit\n- Use a current government-issued ID\n- Ensure your face matches your ID photo\n\nNeed help? Reply to this email or chat with support.",
      ),

      // 6. Send push notification
      n("6", "pushNotification", "Push: Complete your verification", 1240, {
        pushNotificationDetails: {
          title: "Almost there! Finish your verification",
          body: "Complete identity verification to unlock all features. Takes 3 minutes.",
          url: "{kyc_resume_url}",
        },
      }),

      // 7. Slack: Flag compliance team for follow-up
      slackNode(
        "7",
        "Slack: Flag incomplete KYC for compliance",
        1480,
        "Incomplete KYC alert: {user_name} ({user_email}) started KYC 24h ago, stopped at '{step_reached}'. Reason: {blocking_reason}. Reminder sent. Manual follow-up may be needed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // incomplete -> send reminder
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Fintech KYC Started Not Completed** — Re-engages fintech users who started identity verification but abandoned it, with targeted help.\n\n1. Fires when KYC verification starts\n2. Waits 24 hours for completion\n3. Checks KYC status and identifies where user dropped off\n4. If incomplete, sends helpful reminder with common troubleshooting tips\n5. Sends push notification with resume link\n6. Flags compliance team on Slack for manual follow-up\n\n**To activate:** Connect your KYC provider webhook and Slack.`,
  },

  // ===================================================================
  // 128. FINTECH WALLET FUNDED NO FIRST TRANSACTION (7 nodes)
  // ===================================================================

  "Fintech Wallet Funded No First Transaction": {
    nodes: [
      trigger("App webhook: Wallet funded", "webhook_app", {
        source: "App Backend / Payment Processor",
        event: "wallet.funded",
        howItStarts:
          "A user funds their wallet or account but does not make their first transaction within 48 hours",
        setup:
          "Configure your app backend to POST when a wallet is funded for the first time. Include user_id, email, amount_funded, currency, and funding_method.",
        inputFields: [],
      }),

      // 2. Wait 48 hours for first transaction
      n("2", "delayAction", "Wait 48h for first transaction", 280, {
        delayData: { delay: 48, delayType: "hours" },
      }),

      // 3. Check transaction history
      n("3", "hitApi", "Check transaction history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/transactions?limit=1",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            { name: "total", customName: "transaction_count", type: "number" },
            {
              name: "wallet_balance",
              customName: "wallet_balance",
              type: "string",
            },
            { name: "user_email", customName: "user_email", type: "string" },
            { name: "user_name", customName: "user_name", type: "string" },
            { name: "currency", customName: "currency", type: "string" },
          ],
        },
      }),

      // 4. Condition: No transactions yet?
      n("4", "condition", "Still zero transactions?", 760, {
        conditionData: [
          {
            variable: "transaction_count",
            value: "0",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send first transaction nudge
      emailNode(
        "5",
        "Send first transaction nudge",
        1000,
        "{user_email}",
        "Your {currency} {wallet_balance} is ready to use, {user_name}",
        "Hi {user_name},\n\nYou funded your wallet with {currency} {wallet_balance} — nice! But you haven't made your first transaction yet.\n\nHere's what you can do:\n- Send money to a friend\n- Pay a bill\n- Invest in stocks or crypto\n- Set up recurring payments\n\nMake your first transaction: {transaction_url}\n\nBonus: Your first transaction is fee-free!",
      ),

      // 6. Send push notification
      n("6", "pushNotification", "Push: Your wallet is ready", 1240, {
        pushNotificationDetails: {
          title: "Your {currency} {wallet_balance} is waiting",
          body: "Make your first transaction today — it's fee-free! Tap to start.",
          url: "{transaction_url}",
        },
      }),

      // 7. Track in analytics
      n("7", "hitApi", "Track wallet-funded-no-transaction event", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_id}" },
            { key: "event", value: "wallet_funded_no_transaction" },
            {
              key: "properties",
              value:
                '{"wallet_balance":"{wallet_balance}","currency":"{currency}","hours_since_funding":48}',
            },
          ],
          variables: [
            { name: "success", customName: "tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // no transactions -> nudge
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Fintech Wallet Funded No First Transaction** — Re-engages users who funded their wallet but haven't made a first transaction within 48 hours.\n\n1. Fires when wallet is funded\n2. Waits 48 hours for first transaction\n3. Checks transaction history\n4. If zero transactions, sends nudge email with use case suggestions\n5. Sends push notification with fee-free incentive\n6. Tracks event in Segment for analytics\n\n**To activate:** Connect your app backend webhook and Segment.`,
  },

  // ===================================================================
  // 129. FIRST ORDER DELIVERED NO APP INSTALL (6 nodes)
  // ===================================================================

  "First Order Delivered No App Install": {
    nodes: [
      trigger("Ecommerce webhook: First order delivered", "webhook_app", {
        source: "Shopify / Order Management",
        event: "order.delivered",
        howItStarts:
          "A customer's first order is delivered but they have not installed the mobile app",
        setup:
          "Configure your ecommerce platform to POST delivery events for first-time customers. Include customer_id, email, order_id, and has_app_installed flag.",
        inputFields: [],
      }),

      // 2. Check if mobile app is installed
      n("2", "hitApi", "Check app install status", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/customers/{customer_id}/app-status",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "app_installed",
              customName: "app_installed",
              type: "boolean",
            },
            {
              name: "customer_email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "customer_name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "order_total",
              customName: "first_order_total",
              type: "string",
            },
            { name: "device_os", customName: "device_os", type: "string" },
          ],
        },
      }),

      // 3. Condition: App not installed?
      n("3", "condition", "App not installed?", 520, {
        conditionData: [
          {
            variable: "app_installed",
            value: "false",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Send app install nudge email
      emailNode(
        "4",
        "Send app install nudge email",
        760,
        "{customer_email}",
        "Your order is delivered! Get 15% off your next with our app",
        "Hi {customer_name},\n\nGreat news — your order has been delivered! We hope you love it.\n\nGet our app for an even better experience:\n- Track orders in real-time\n- Exclusive app-only deals\n- Faster checkout with saved preferences\n- 15% off your next order when you install\n\nDownload now:\niOS: {ios_app_url}\nAndroid: {android_app_url}",
      ),

      // 5. Send SMS with deep link
      n("5", "sendSMS", "SMS: App install with deep link", 1000, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message:
            "Your order just arrived! Download our app for 15% off your next purchase: {app_deep_link}",
        },
      }),

      // 6. Track in analytics
      n("6", "hitApi", "Track delivered-no-app event", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{customer_id}" },
            { key: "event", value: "first_order_delivered_no_app" },
            {
              key: "properties",
              value:
                '{"order_total":"{first_order_total}","device_os":"{device_os}"}',
            },
          ],
          variables: [
            { name: "success", customName: "tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // app not installed -> nudge
      e("4", "5"),
      e("5", "6"),
    ],
    summary: `**First Order Delivered No App Install** — Nudges first-time customers to install the mobile app when their first order is delivered, with an app-exclusive discount.\n\n1. Fires when first order is delivered\n2. Checks if customer has installed the mobile app\n3. If no app, sends email with app download links and 15% off incentive\n4. Sends SMS with deep link for immediate install\n5. Tracks event in Segment\n\n**To activate:** Connect your ecommerce webhook and Segment.`,
  },

  // ===================================================================
  // 130. FIRST-WEEK HABIT-BUILDING NUDGE (7 nodes)
  // ===================================================================

  "First-Week Habit-Building Nudge": {
    nodes: [
      trigger("App webhook: User completed first key action", "webhook_app", {
        source: "App Backend",
        event: "user.first_key_action",
        howItStarts:
          "A user completes their first key action and enters the critical habit-building window (first 7 days)",
        setup:
          "Configure your app to POST when a user completes their first key action. Include user_id, email, action_type, and action_timestamp.",
        inputFields: [],
      }),

      // 2. Send Day-1 congratulations + next step
      emailNode(
        "2",
        "Day 1: Congratulations + next step",
        280,
        "{user_email}",
        "You just took your first step — here's how to build on it",
        "Hi {user_name},\n\nCongrats on completing your first {action_type}! You're part of the 40% of users who take action in week one — and they go on to become our most successful users.\n\nYour next step: {next_action_suggestion}\nTry it now: {next_action_url}\n\nWe'll check in over the next week to help you build a habit.",
      ),

      // 3. Wait 2 days
      n("3", "delayAction", "Wait 2 days", 520, {
        delayData: { delay: 2, delayType: "days" },
      }),

      // 4. Check Day-3 engagement
      n("4", "hitApi", "Check Day-3 activity level", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourapp.com/v1/users/{user_id}/activity-summary?days=3",
          headers: [{ key: "Authorization", value: "Bearer {app_api_key}" }],
          body: [],
          variables: [
            {
              name: "actions_count",
              customName: "actions_day3",
              type: "number",
            },
            {
              name: "sessions_count",
              customName: "sessions_day3",
              type: "number",
            },
            { name: "streak_days", customName: "streak_days", type: "number" },
            { name: "user_email", customName: "user_email", type: "string" },
            { name: "user_name", customName: "user_name", type: "string" },
          ],
        },
      }),

      // 5. Condition: Engaged (2+ actions)?
      n("5", "condition", "Engaged (2+ actions in 3 days)?", 1000, {
        conditionData: [
          {
            variable: "actions_day3",
            value: "2",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Engaged: Streak encouragement
      emailNode(
        "6",
        "Engaged: Streak encouragement email",
        1240,
        "{user_email}",
        "You're on a {streak_days}-day streak! Keep building the habit",
        "Hi {user_name},\n\nYou're building a great habit — {actions_day3} actions across {sessions_day3} sessions in just 3 days!\n\nStreak: {streak_days} days\n\nKeep it going through day 7 and you'll unlock our 'Power User' badge. Users who maintain a 7-day streak retain 5x longer.\n\nKeep going: {dashboard_url}",
        380,
      ),

      // 7. Disengaged: Gentle re-engagement nudge
      emailNode(
        "7",
        "Disengaged: Gentle nudge email",
        1240,
        "{user_email}",
        "Quick check-in: Need help with your next step?",
        "Hi {user_name},\n\nYou had a great start with your first {action_type}! It's been a few days — would a quick tip help?\n\nMost users find success by doing one small action each day in their first week. Here's something you can do in 2 minutes: {quick_action_url}\n\nOr book a 5-minute setup call: {setup_call_url}",
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // engaged -> streak encouragement
      e("5", "7", "output_2"), // disengaged -> gentle nudge
    ],
    summary: `**First-Week Habit-Building Nudge** — Guides new users through their critical first week with Day-1 congratulations and Day-3 branching based on engagement level.\n\n1. Fires when user completes first key action\n2. Day 1: Sends congratulations with next step suggestion\n3. Waits 2 days and checks Day-3 engagement metrics\n4. Engaged users (2+ actions): get streak encouragement and Power User badge incentive\n5. Disengaged users: get gentle nudge with quick-win action and setup call offer\n\n**To activate:** Connect your app backend webhook.`,
  },
};
